/** Individual AI and shared command builders. Source: elizacontroller.cpp/playercontroller.cpp. */

import { Vector3 } from 'three';
import { GOAL } from './ball';
import { BALL, BALL_PREDICTION_MS, PITCH, TICK_MS } from '../core/constants';
import {
  CHIP_OVERSHOOT_M,
  STANDING_REACH_RATIO,
  chipDirection,
  solveChip,
  type ChipFlight,
} from './chip';
import {
  angle2D,
  clamp,
  curve,
  normalizedClamp,
  normalizedOr,
  normalizedOrZero,
  rotate2D,
  signSide,
} from '../core/math';
import { hashedRandom } from '../core/random';
import type { PlayerCommand } from './anim/select';
import {
  DRIBBLE_VELOCITY,
  DRIBBLE_WALK_SWITCH,
  IDLE_DRIBBLE_SWITCH,
  IDLE_VELOCITY,
  SPRINT_VELOCITY,
  WALK_VELOCITY,
  clampVelocity,
  rangeVelocity,
} from './anim/velocity';
import { DEFAULT_TOUCH_OFFSET_MS } from './anim/touch';
import { passPowerForSpeed } from './anim/touchvector';
import { ROLE_OFFENSIVENESS } from './formation';
import { TouchType, VELOCITY_HISTORY_SECONDS, type Match, type MatchPlayer } from './match';
import type { MentalImage, MentalImagePlayer } from './mentalimage';
import { adaptedFormationPosition, applyOffsideTrap, offsideLine } from './teamai';
import { timeNeededForDistanceMs } from './possession';
import type { RestartState } from './referee';
import { SetPiece } from './setpiece';

const FORWARD = new Vector3(0, -1, 0);

export interface ChaseOptions {
  position: Vector3;
  directionVec: Vector3;
  target: Vector3;
  standoff: number;
}

export type HumanActionIntent =
  | { type: 'shortpass' | 'longpass' | 'highpass' | 'shot'; power: number }
  | { type: 'sliding'; power: number }
  /**
   * Both feet at an opponent, with no interest in the ball. A straight red if it lands.
   *
   * It has a drawing of its own now — `special/flying_kick`, written by this project — and that
   * is the whole point of it: it used to be given a sliding tackle's clip, so the two acts were
   * indistinguishable to watch. The leap leaves the ground, both feet go in at chest height,
   * and it travels three metres. Everything around the clip stays as it was: it is aimed at the
   * man rather than at the ball, it is given whether or not the ball is anywhere near, and the
   * referee is told outright that it was violent instead of scoring it like an honest challenge.
   */
  | { type: 'flyingkick'; power: number }
  /**
   * A punch, on a key of its own. `special/punch`, also this project's own drawing.
   *
   * The one act in the game with no football in it at all — no ball, no challenge, nothing to
   * judge but the fact that he swung. A straight red, told to the referee directly, because
   * nothing he can see would tell him: the engine has no offence that is not two men's legs
   * meeting.
   */
  | { type: 'punch'; power: number }
  /**
   * A body swerve: throw the hips and shoulders one way, and go the other.
   *
   * The first thing this project has drawn that is football rather than violence, and the only
   * one that beats a man without touching the ball at all — which is what a feint IS. Nothing
   * in the engine has to be told that the defender was deceived: every player reads everybody
   * else through a mental image delayed by his own reaction time, so a man who commits to the
   * fake is a man answering where the ball-carrier was a fifth of a second ago. The clip is
   * the whole of the feature.
   */
  | { type: 'feint'; power: number };

/**
 * One controller's state after it has been sampled by the fixed 10 ms tick.
 *
 * Actions may stay present for several ticks: the animation runtime only calls the
 * controller when the current clip is interruptible, and the human-side buffer must survive
 * until that happens.
 */
export interface HumanPlayerIntent {
  direction: Vector3;
  velocity: number;
  action: HumanActionIntent | null;
  pressure: boolean;
  superCancel: boolean;
  keyboard: boolean;
  /**
   * How recently this player was handed to the person driving him: 1 at the instant of the
   * switch, nothing again three tenths of a second later (`playercontroller.cpp:59-63`).
   * Besides smoothing the handover itself, it is what lets a man just switched to set off
   * after a ball played to him while somebody else is still nominally on it.
   */
  switchBias: number;
  /**
   * The long-touch key: knock it well ahead and go after it.
   *
   * Carrying the ball at a sprint already lengthens every touch, because the dribble solver
   * plants the ball where the speed being asked for says it should be. This is the deliberate
   * shove on top of that, for the yard of space a person wants when he is already past his man
   * and the only thing between him and the goal is ground to cover.
   */
  longTouch: boolean;
  /**
   * The shield key: stand over the ball and hold him off.
   *
   * Held rather than tapped, and it is the one act here that is really a STATE — a man shields
   * for as long as he needs support to arrive, and the clip is seven tenths of a second, so a
   * tap would give him one and drop him out of it. Nothing else in the game reads it.
   */
  shield: boolean;
  /**
   * The chip key: lift it over the keeper.
   *
   * The only strike in the game whose power a person does not charge. Every other one turns how
   * long he held the key into how hard the ball is hit; a chip has exactly one right speed for
   * the range it has to cover, so holding the key longer could only make it worse. He aims it
   * and the ball is solved for him.
   */
  chip: boolean;
}

export function chaseCommand(options: ChaseOptions): PlayerCommand {
  const toTarget = options.target.clone().sub(options.position).setZ(0);
  const distance = toTarget.length();
  let velocity = SPRINT_VELOCITY;
  if (distance < options.standoff) velocity = 0;
  else if (distance < options.standoff + 1.5) velocity = DRIBBLE_VELOCITY;
  else if (distance < options.standoff + 6) velocity = WALK_VELOCITY;
  const direction = normalizedOr(toTarget, options.directionVec);
  return movementCommand(direction, velocity, options.target);
}

export function holdCommand(position: Vector3, lookAt: Vector3): PlayerCommand {
  return movementCommand(normalizedOr(lookAt.clone().sub(position), FORWARD), 0, lookAt);
}

/**
 * `specialvar1` (`gamedefines.hpp:176-181`): which of the actions that are not football.
 * 3 is the referee showing a card — asked for by his own body's controller rather than from
 * here, since only he ever plays it; see `SPECIAL_SHOWCARD` in `src/sim/officials.ts`.
 */
const CELEBRATION_HAPPY = 1;
const CELEBRATION_SAD = 2;
/**
 * The two this project drew itself, at the end of the library (indices 1452-1455).
 *
 * Naming a number is the ONLY way to reach one of these. The hard filter compares
 * `specialvar1` against every clip and every football clip carries zero, so a command that
 * does not name one can never arrive at a punch by accident, however its geometry lines up.
 */
const SPECIAL_PUNCH = 4;
const SPECIAL_FLYING_KICK = 5;
/**
 * The feint, `special/feint`. Seven and not six: six is already the reaction to being struck,
 * which tells the two apart by a SECOND number, and while the filter would in fact keep them
 * apart — the struck clips carry a second number and this one does not — two unrelated acts
 * sharing a name is how a later change breaks one of them.
 *
 * It sorts before the violent clips in the library, which nothing else this project has added
 * does: the authored clips are appended in name order and `feint` comes before `flying_kick`.
 * That moves their indices and moves nothing else, since each is reachable only by naming its
 * own number.
 */
const SPECIAL_FEINT = 7;
/** The shield, `special/shield`. */
const SPECIAL_SHIELD = 8;

/**
 * `specialvar2`: which register of the celebration. The library ships exactly two happy ones
 * and the difference between them is the man's own speed, not a matter of taste: the standing
 * one is drawn from a standstill and the extreme one is drawn coming in at seven metres a
 * second and decelerating to nothing over three seconds. So asking for the extreme one while
 * standing still selects nothing at all — the hard filter will not start a sprint clip on a
 * stationary man — and the two numbers below always travel with a speed to match.
 */
const CELEBRATION_STANDING = 1;
const CELEBRATION_RUNNING = 2;

/**
 * When a celebration is asked for, measured from the goal — `elizacontroller.cpp:1121`.
 *
 * Not from the first tick: for the first two seconds the scoring side is running away from
 * where the ball went in, which is what the movement command below is for, and it is only
 * once they are up to speed that the clip that decelerates out of a sprint has anything to
 * decelerate from.
 */
const CELEBRATION_START_MS = 2000;
const CELEBRATION_END_MS = 4000;

/** How many of the scoring side who would otherwise stand still run off with the scorer. */
const CELEBRATION_RUNNERS_SHARE = 1 / 3;

/**
 * A goal has gone in and the referee has not yet arranged the kick-off.
 *
 * The original gates the whole celebration on `!match->IsInPlay() && match->IsGoalScored()`
 * (`elizacontroller.cpp:78-82`) and returns immediately afterwards, so a celebrating player is
 * doing nothing else whatever. `goalScored` is raised the moment the ball crosses the line and
 * lowered by `Match::ResetSituation` (`match.cpp:654,980`), which is the original's name for
 * what this port does when a restart is prepared: everybody is teleported into position and
 * every clip in flight is thrown away by `HumanoidAnim.resetPosition`.
 *
 * THAT TELEPORT IS WHY THIS CANNOT HANG A MATCH, and it is the only reason. The football clock
 * does not advance while play is stopped, so a man who wandered off and never came back would
 * stop the match for ever with nothing on screen to say so. Nobody has to come back: the
 * window is bounded by a restart that was already scheduled when the goal was given, and
 * preparing it puts all twenty-two men on their marks and stands them in an idle clip whatever
 * they were in the middle of. A test pins the whole sequence.
 */
function goalCelebration(match: Match): RestartState | null {
  const restart = match.pendingRestart;
  if (restart === null) return null;
  // `endPhase` marks the kick-off that opens a period rather than one that follows a goal.
  if (restart.type !== SetPiece.KickOff || restart.endPhase || restart.prepared) return null;
  return match.lastGoalTeamId >= 0 ? restart : null;
}

/**
 * The whole of `ElizaController::_AddCelebration` (`elizacontroller.cpp:1079-1145`).
 *
 * Everybody on the pitch is given something to do. The side that scored heads for the corner
 * flag at the end the ball is in, on the side of the pitch the scorer is on; the scorer
 * himself goes flat out and is handed the celebration that runs away and decelerates; anybody
 * within twenty metres of him goes to him instead; and the side that conceded stands still and
 * is handed the miserable one. Two commands come back, in the original's order: the
 * celebration, which is only offered in the window above, and behind it the movement, which is
 * what carries him there and what plays when no celebration clip fits.
 *
 * WHAT IS OURS RATHER THAN THE ORIGINAL'S: one man in three of the scoring side who would have
 * stood rooted to the spot sets off for the corner with the scorer and gets the big celebration
 * too. The original leaves every one of them standing, so the extreme clip is reachable by
 * exactly one man in the match and the other ten do the same small salute. Which of them runs
 * is a hash of the run's seed, the player and the tick the goal went in — the same shape as the
 * AI's own pass and shot coin flips (`deterministicRandom` at the foot of this file) — so it is
 * fixed for the whole of that celebration, differs from goal to goal and from player to player,
 * and replays exactly on the same seed. Nothing in the simulation may draw from the platform.
 *
 * @param allowMovement false for a player a person is driving: he celebrates where he stands
 *                      rather than being walked about the pitch without having asked for it.
 */
export function celebrationCommands(
  match: Match,
  self: MatchPlayer,
  allowMovement: boolean,
): PlayerCommand[] | null {
  const restart = goalCelebration(match);
  if (restart === null) return null;

  const team = match.team(self.teamId);
  // The original reads his OWN side's last toucher, which for the side that scored is the
  // scorer (`elizacontroller.cpp:1082`). For the side that conceded it is whoever of theirs
  // touched it last, and it is used only to pick which corner they face.
  const scorer = team.lastTouchPlayer;

  const xSide = match.ball.predict(0).x > 0 ? 1 : -1;
  const ySide = scorer ? (scorer.position.y > 0 ? 1 : -1) : team.side;
  const corner = new Vector3(PITCH.halfLength * xSide, PITCH.halfWidth * ySide, 0);

  let direction = normalizedOr(corner.clone().sub(self.position).setZ(0), self.directionVec);
  let velocity = clampVelocity(corner.distanceTo(self.position) / 4);
  // A thousand metres up his own nose. It must not be a point he is standing on: a look-at of
  // almost no length hands the selector a body angle out of the noise in it.
  let lookAt = self.position.clone().addScaledVector(self.directionVec, 1000);

  let celebrationType: number;
  let register = CELEBRATION_STANDING;

  if (match.lastGoalTeamId !== self.teamId) {
    celebrationType = CELEBRATION_SAD;
    velocity = IDLE_VELOCITY;
  } else {
    celebrationType = CELEBRATION_HAPPY;
    if (scorer === self) {
      register = CELEBRATION_RUNNING;
      velocity = SPRINT_VELOCITY;
    } else if (scorer) {
      const toScorer = scorer.position.distanceTo(self.position);
      if (toScorer < 20) {
        direction = normalizedOr(scorer.position.clone().sub(self.position).setZ(0), direction);
        velocity = clampVelocity(toScorer / 2);
      } else if (
        // Not the goalkeeper. He is the length of the pitch from the corner flag and six
        // seconds of sprinting does not get him a third of the way there, so all it produces
        // is a keeper wandering upfield after a goal in the eighth minute. The original leaves
        // him standing exactly as it leaves every other far team-mate standing.
        self.role !== 'GK' &&
        hashedRandom(restart.stopTimeMs / TICK_MS, self.id, 12, match.randomSeed) <
          CELEBRATION_RUNNERS_SHARE
      ) {
        register = CELEBRATION_RUNNING;
        velocity = SPRINT_VELOCITY;
      } else {
        velocity = IDLE_VELOCITY;
      }
      // His own position offset along the SCORER's facing, which is what the original writes
      // (`elizacontroller.cpp:1111`) — not the scorer's position. It turns the whole side the
      // same way as the man who scored.
      lookAt = self.position.clone().addScaledVector(scorer.directionVec, 100);
    }
  }

  // A person's man stays where he is, so he can only ever be offered the standing celebration.
  if (!allowMovement) {
    velocity = IDLE_VELOCITY;
    register = CELEBRATION_STANDING;
  }

  const commands: PlayerCommand[] = [];
  const sinceGoalMs = match.timeMs - restart.stopTimeMs;
  if (sinceGoalMs > CELEBRATION_START_MS && sinceGoalMs < CELEBRATION_END_MS) {
    commands.push({
      // Deliberately the one `special` type rather than a type of its own: see the note on
      // `specialVar1` in `src/sim/anim/select.ts`.
      desiredFunctionType: 'special',
      // Both false, as the original's celebration command has them
      // (`elizacontroller.cpp:1127-1128`). It plays exactly as it was drawn: the physics bias
      // for a special clip is zero, so its own root track is the movement and there is no
      // solver underneath it to bend.
      useDesiredMovement: false,
      desiredDirection: direction.clone(),
      desiredVelocityFloat: velocity,
      useDesiredLookAt: false,
      desiredLookAt: lookAt.clone(),
      specialVar1: celebrationType,
      specialVar2: register,
    });
  }
  commands.push(
    allowMovement
      ? movementCommand(direction, velocity, lookAt)
      : holdCommand(self.position, match.ball.position),
  );
  return commands;
}

/**
 * A sent-off man's whole command for as long as he is still worth drawing — tranche 1 of
 * `docs/design/violence-and-variety.md`, the other half of "the card, shown". `Match.sendOff`
 * (`src/sim/match.ts`) already pulls him out of his side's roster and every gameplay read —
 * AI, collisions, possession — on the instant it always did; this only decides what the render
 * side sees him doing for `PLAYER_WALK_OFF_MS` afterwards, which is the ordinary movement
 * solver and animation selector every other player already goes through, aimed at the nearer
 * touchline. No gait of its own to author. Once `MatchPlayer.isDeparting` says his window is
 * up, this is nothing at all — exactly what an ordinary dismissal used to be from the first
 * tick, before this existed.
 *
 * Called from both `playerCommands` and `humanPlayerCommands`: once he is off, nobody is
 * driving him any more, human or computer, so both dispatchers answer the same way.
 */
export function departingPlayerCommands(match: Match, self: MatchPlayer): PlayerCommand[] {
  if (!self.isDeparting(match.timeMs)) return [];
  // Sideways to the nearer touchline and a few metres past it, holding his own position along
  // the length of the pitch — the simplest reading of "walking off" that needs no pathfinding
  // around anybody else still out there. `|| 1` only matters for a man dismissed standing
  // exactly on the centre line: Math.sign of a real offset always picks a real side, and that
  // is the one position where it would not.
  const touchlineY = Math.sign(self.position.y || 1) * (PITCH.halfWidth + 4);
  const target = new Vector3(self.position.x, touchlineY, 0);
  const direction = normalizedOr(target.clone().sub(self.position), self.directionVec);
  return [movementCommand(direction, WALK_VELOCITY, target)];
}

/**
 * Complete AI queue, best command first. The last two parameters remain for old callers;
 * the real controller derives both interception and spacing from the mental image.
 */
export function playerCommands(
  match: Match,
  self: MatchPlayer,
  _legacyBallTarget?: Vector3,
  _legacyStandoff?: number,
): PlayerCommand[] {
  if (!self.active) return departingPlayerCommands(match, self);
  if (!match.inPlay) {
    // The one thing that happens while play is stopped and nobody has been placed yet
    // (`elizacontroller.cpp:78-82`, ahead of every other branch and returning outright).
    return (
      celebrationCommands(match, self, true) ?? [holdCommand(self.position, match.ball.position)]
    );
  }
  // A keeper holding the ball is handled exactly like a set-piece taker (`elizacontroller.cpp:135`):
  // getting rid of it is the only thing he is doing.
  if (match.ballRetainer === self) return retainerCommands(match, self);
  if (match.inSetPiece) {
    return match.setPieceTaker === self
      ? setPieceCommands(match, self)
      : [holdCommand(self.position, match.ball.position)];
  }

  const image = match.mentalImageFor(self);
  const queue: PlayerCommand[] = [];
  let desired: DesiredMovement;
  let forceMagnet = false;
  let extraHaste = false;
  // The keeper's own positioning replaces the ball magnet outright rather than feeding it,
  // in the two cases the original calls manual movement (`elizacontroller.cpp:288-290`).
  let manualMovement = false;

  if (self.role === 'GK') {
    const goalie = goalkeeperMovement(match, self, image);
    const deflect = keeperDeflectCommand(match, self, image);
    if (deflect) queue.push(deflect);

    manualMovement =
      goalkeeperGoalThreat(match, self, image).bound || match.designatedPossessionPlayer !== self;

    // With the ball at his feet he is given the same passing, shooting and dribbling choices
    // as anybody else (`elizacontroller.cpp:300-302`). Without this he can only shepherd it
    // around, and what he shepherds it towards is his own goal line.
    desired = { direction: self.directionVec.clone(), velocity: IDLE_VELOCITY, lookAt: goalie.lookAt };
    if (self.timeNeeded.usualMs < 1000 && match.designatedPossessionPlayer === self) {
      const onBall = onBallCommands(match, self, image);
      queue.push(...onBall.commands);
      desired = onBall.movement;
    }
    if (manualMovement) desired = goalie;
  } else if (match.designatedPossessionPlayer === self) {
    const onBall = onBallCommands(match, self, image);
    queue.push(...onBall.commands);
    desired = onBall.movement;

    // The two dribbling flourishes, BELOW the pass and the shot he has just been offered and
    // above everything else. A man with a team-mate better placed plays the ball to him; these
    // are for the man with nowhere to put it. Both are gated on situations measured over whole
    // matches rather than chosen — see each one's own note.
    const swerve = computerSwerveCommand(match, self, image, onBall.movement);
    if (swerve) queue.push(swerve);
    const shield = computerShieldCommands(match, self);
    if (shield) {
      queue.push(...shield.commands);
      // A shield ends in a stop, and the stop is the last word on where he goes: everything
      // below it in the queue is unreachable. So what he is ASKING for is that stop, not the
      // dribble the tactics wanted a moment ago — otherwise a ball contact committed a quarter
      // of a second before he planted would knock the ball away from under him.
      desired = shield.stance;
    }
  } else {
    desired = offBallMovement(match, self, image);
    // Leaving position to go and win the ball back overrides holding shape.
    const hunt = huntMovement(match, self, image, desired);
    if (hunt) {
      desired = hunt.movement;
      forceMagnet = true;
      extraHaste = hunt.extraHaste;
    }
    // An ordered squeeze overrides both: the nominated man goes at the ball regardless of
    // whether he would have chosen to. `elizacontroller.cpp:307-313`.
    if (underTeamPressureOrder(match, self)) forceMagnet = true;
  }

  // What the computer is asking for this instant, recorded before the ball magnet bends it and
  // before any of the commands below are built — `elizacontroller.cpp:315-318` in that order.
  // A carrying touch blends it with the wish its clip was started for.
  self.controllerVelocityFloat = desired.velocity;

  // The second half of a wall pass, ahead of everything below: if a human just knocked the
  // ball to this man as his own nominated run, give it straight back into that human's path
  // rather than let the ordinary queue trap it. See `wallPassReturnCommand`. Unreachable, or
  // nothing armed, it is null and changes nothing below.
  const wallPassReturn = wallPassReturnCommand(match, self, image, desired);
  if (wallPassReturn) queue.unshift(wallPassReturn);

  if (!match.inSetPiece) {
    const ballControl = ballControlCommand(match, self, image, desired);
    if (ballControl) queue.push(ballControl);
    // Ahead of the header, because the two fire in the same situation and the overhead kick is
    // the rarer, better answer to it — and because it can reach no clip the header could have
    // had. See `bicycleCommands`.
    queue.push(...bicycleCommands(match, self, image, desired));
    // Ahead of the trap, because a ball above the chest is one the trap cannot reach either.
    queue.push(...headerCommands(match, self, image, desired));
    const trap = trapCommand(match, self, image, desired);
    if (trap) queue.push(trap);
    const interfere = interfereCommand(match, self, image, desired);
    if (interfere) queue.push(interfere);
    const sliding = slidingCommand(match, self, image);
    if (sliding) queue.push(sliding);
  }
  const fallbackMovement = manualMovement
    ? desired
    : automaticMovement(match, self, image, desired, { forceMagnet, extraHaste });
  queue.push(
    movementCommand(
      fallbackMovement.direction,
      fallbackMovement.velocity,
      fallbackMovement.lookAt,
    ),
  );
  return queue;
}

/**
 * How long a restart a person is standing over may go untaken before his man takes it himself.
 *
 * Simulation time, not football time — the football clock is stopped, which is the whole
 * problem. Fifteen seconds is far longer than anyone spends deciding where to put a corner and
 * far shorter than the "is this thing broken?" that a stopped match really is. The original has
 * no such deadline and will wait for ever.
 */
const UNTAKEN_RESTART_MS = 15_000;

/**
 * How much further in front of his foot the long-touch key plants the ball, in metres.
 *
 * CHOSEN, NOT DERIVED, and the response to it is steep and not monotonic, so it was measured
 * rather than reasoned about. Same man, same run, five seconds of holding sprint and one arrow,
 * averaged over four seeds:
 *
 *     off      30.8 m covered, ball 1.13 m in front of him, still his on 72% of ticks
 *     1.2      33.5 m covered, ball 1.06 m in front of him, still his on 62%
 *     1.5      21.4 m covered, ball 6.75 m in front of him, still his on 29%
 *     1.8      29.3 m covered, ball 6.26 m in front of him, still his on 22%
 *
 * So this is worth about a tenth more ground for a tenth less of the ball — he stops braking to
 * keep it under his feet — and somewhere past 1.4 it stops being a long touch and becomes
 * kicking it away and chasing, which covers LESS ground because he spends the five seconds
 * running after a ball he no longer has. Raise it and a person will lose the ball; that is the
 * trade, and it is a matter of feel, so it is one number in one place.
 */
const LONG_TOUCH_METRES = 1.2;

/**
 * Human queue in `HumanController::RequestCommand` order.
 *
 * The runtime still owns the interruption gate and animation availability check. This
 * function only states the ordered preferences to try when the runtime asks.
 */
export function humanPlayerCommands(
  match: Match,
  self: MatchPlayer,
  intent: HumanPlayerIntent,
): PlayerCommand[] {
  // Once he is off, nobody is driving him any more — a person's controls stop reaching him
  // exactly as the computer's AI does, and both dispatchers hand him the same walk to the
  // touchline. See `departingPlayerCommands`.
  if (!self.active) return departingPlayerCommands(match, self);
  if (!match.inPlay && !match.inSetPiece) {
    // He celebrates a goal like everybody else, but standing where he is: he has no controls
    // while play is stopped, and walking a man about the pitch on his behalf is not something
    // he asked for. The false is that.
    return (
      celebrationCommands(match, self, false) ?? [holdCommand(self.position, match.ball.position)]
    );
  }
  if (match.inSetPiece) {
    if (match.setPieceTaker !== self) return [holdCommand(self.position, match.ball.position)];
    // None of the three violent acts takes a restart, and none is allowed at one: nobody may
    // move while play is stopped, so a lunge at a man standing in a wall would be nonsense.
    // They fall through to the wait below, exactly as pressing nothing does.
    if (
      !intent.action ||
      intent.action.type === 'sliding' ||
      intent.action.type === 'flyingkick' ||
      intent.action.type === 'punch' ||
      intent.action.type === 'feint'
    ) {
      // A restart is not on any clock. The football clock does not advance while play is
      // stopped and the referee waits on the taker for ever, so a person who does not know
      // which key plays the ball — or who has simply walked away — stops the match dead and
      // nothing ever un-stops it. After long enough to rule out somebody thinking, his man
      // takes it himself, exactly as the computer's taker would.
      const startedMs = match.pendingRestart?.startTimeMs ?? match.timeMs;
      if (match.timeMs - startedMs >= UNTAKEN_RESTART_MS) return setPieceCommands(match, self);
      return [holdCommand(self.position, match.ball.position)];
    }
    // AND HE HAS TO BE ABLE TO WALK ONTO IT. A restart puts its taker 2.3 m behind the ball
    // (`prepareSetPiece`) for everything except the kick-off and the throw-in, which put him
    // at 0.3 m. No strike animation reaches 2.3 m, so a strike command on its own selects
    // nothing at all: the person pressed the pass key at a corner, at a goal kick and at a
    // free kick and NOTHING WHATEVER HAPPENED — no movement, no touch, not even a wrong one —
    // until the fifteen-second rule above took it for him. Measured: 18.7 s from the award of
    // a corner to it being taken, with the pass key tapped throughout, against 2.4 s for a
    // kick-off. The computer's taker never had this because its queue has carried the same
    // walk-onto-the-ball movement behind the strike all along (`setPieceCommands`); this is
    // that line, and nothing more.
    const image = match.mentalImageFor(self);
    return [
      humanStrikeCommand(match, self, image, intent, intent.action),
      ballControlMovement(self, image, WALK_VELOCITY),
    ];
  }

  const image = match.mentalImageFor(self);
  const manualDirection = bendWhileCarrying(self, normalizedOr(intent.direction, self.directionVec));
  const manual: DesiredMovement = {
    direction: manualDirection,
    velocity: intent.velocity,
    // Where he is to face, which is what sets his body angle when a clip is chosen.
    //
    // Watching the ball is right while he is chasing it and WRONG THE MOMENT IT IS HIS: the
    // ball is then twenty centimetres from his toes, so "look at the ball" is a direction of
    // almost zero length and his body angle comes out of the noise in it. Measured in a
    // running match, holding one arrow: he covered 2.4 m in a second and a half, at 0.14 of
    // the way he was pointed — a man shuffling sideways at walking pace who has plainly
    // stopped answering the controls. The computer's dribbler never had this, because it
    // looks ten metres up its own running line (`dribbleMovement` below), and its players
    // carried the ball at 4.65 m/s in the same match. This is that same line.
    lookAt: self.hasBestPossession
      ? self.position.clone().addScaledVector(manualDirection, 10)
      : image.ballPrediction(500).setZ(0),
  };
  const touchMovement = humanTouchMovement(self, manual, intent.action !== null);
  const queue: PlayerCommand[] = [];

  // Ahead of every action key, because it is a state rather than an act: a man holding
  // somebody off is not also passing, and the two commands it returns are a stop, so nothing
  // below could be reached anyway. Refused — no ball, or nobody on him — the key does nothing
  // and the ordinary queue runs.
  if (intent.shield && canShield(match, self)) return shieldCommands(match, self).commands;

  // The chip is a strike, so it sits with the action keys rather than above them — but ahead of
  // them, because a person who is pressing it has asked for one particular ball and not for
  // whichever of pass and shot he was already holding. Refused — no ball at his feet, or he is
  // pointed away from the goal — the press does nothing and the ordinary queue runs.
  if (intent.chip) {
    const chip = humanChipCommand(match, self, intent, touchMovement);
    if (chip) queue.push(chip);
  }

  if (intent.action) {
    if (intent.action.type === 'flyingkick') {
      queue.push(...flyingKickCommands(match, self, manual));
    } else if (intent.action.type === 'punch') {
      // Everything the queue below it does is football — controlling the ball, trapping it,
      // getting in the way of it — and a man swinging at somebody is doing none of it. The
      // stop inside `punchCommands` is also the last word on where he goes, so nothing behind
      // it would ever be reached anyway.
      return punchCommands(match, self, manual);
    } else if (intent.action.type === 'feint') {
      // Only from a man who has the ball and is not already swerving. Refused, the press
      // falls through to the ordinary queue below rather than freezing him at dribbling pace
      // waiting for a clip that will never be offered.
      if (canFeint(match, self)) return feintCommands(self, image, manual);
    } else if (intent.action.type === 'sliding') {
      queue.push(actionCommand('sliding', manual));
    } else {
      queue.push(humanStrikeCommand(match, self, image, intent, intent.action));
    }
  }

  const ballControl = ballControlCommand(match, self, image, touchMovement);
  if (ballControl) {
    // The long touch only means anything on a ball he already has. Asked for on one he is
    // still chasing it would push the ball away from him at the moment he reaches it.
    //
    // A maxed-charge shot with nobody marking him earns the identical knock-ahead: the person
    // has not asked for it with the long-touch key, but it is the same "push it a stride ahead
    // and go at it at a run" flourish an unhurried, unpressured striker would actually take
    // before he hits it, so it reuses the same primitive and the same tuned distance rather
    // than inventing a second one.
    if (
      (intent.longTouch || unmarkedMaxChargeShot(match, self, intent.action)) &&
      self.hasBestPossession
    ) {
      ballControl.frontOfFootOffset = LONG_TOUCH_METRES;
    }
    queue.push(ballControl);
  }
  const trap = trapCommand(match, self, image, touchMovement);
  if (trap) queue.push(trap);
  const interfere = interfereCommand(match, self, image, touchMovement, intent.pressure);
  if (interfere) queue.push(interfere);

  const fallback =
    intent.superCancel && !self.hasBestPossession
      ? manual
      : automaticMovement(match, self, image, manual, { humanSwitchBias: intent.switchBias });
  queue.push(movementCommand(fallback.direction, fallback.velocity, fallback.lookAt));
  return queue;
}

/**
 * How far off his own running line a man carrying the ball may be asked to go at once, and the
 * turn above which he is asked for the whole thing anyway.
 *
 * There is a CLIFF in what a turn with the ball costs, and it sits between 20 and 30 degrees.
 * A person sprinting with the ball at 6.35 m/s and asked for a change of direction arrives at 20
 * degrees having lost NOTHING — still 6.35 — at 30 he is down to 4.22, and at 45 to 3.69, a man
 * who has braked to a walk to make a quarter turn. The library is why: every clip that turns a
 * sprinting carrier past the cliff decelerates and its own name says so —
 * `ballcontrol/sprint/045_decel`, `090_decel_decel`, `135_decel_decel_decel`. There is no
 * bending at pace above the cliff, and the request is snapped onto a table whose only entries
 * are 0, 20, 45, 90 and 135, so what is really being chosen here is which of those he is given.
 *
 * Three sizes of turn, and the whole of what this does is pick the step:
 *
 *   up to 70 degrees   bends of 22, under the cliff. He arrives with all of his speed —
 *                      6.35 m/s at 45 against 3.93, and 6.28 at 60 against 2.94. It costs
 *                      time: 675 ms to come round at 45 against 490, and 1190 at 60 against 575.
 *   70 to 80           one 45-degree cut and then bends, which is the only thing between the
 *                      library's 45 clip and its 90. At 75 that is 3.93 m/s against 2.88, for
 *                      930 ms against 680.
 *   past 80            handed over whole. `ballcontrol/sprint/090_decel_decel` plants and takes
 *                      him from 6.5 m/s to 2.6 over half a second, which is a proper cut.
 *
 * WHAT THIS DOES NOT DO, and two measurements said otherwise before they were re-run against
 * varied situations: it does not change whether he keeps the ball, and he was never over-turning.
 * Possession over the two seconds after a turn is 0.71 to 0.80 whatever the angle and whichever
 * step is used, every comparison inside noise — it answers to whether an opponent happens to be
 * closing in, not to the turn. And measured overshoot is 0 to 3 degrees at every angle.
 *
 * The step size is not delicate but it is not free either: 18, 22 and 26 all leave him at his
 * full 6.35 m/s and settle within 30 ms of each other, and 32 falls off the cliff and puts him
 * back down to 3.77.
 *
 * This cannot stall or oscillate, which the three shapes tried before it all did. The step is
 * measured from his LIVE heading and always points at what he asked for, so the part still owed
 * him only ever shrinks, and he is asked for a real turn on every tick until it reaches zero.
 *
 * MEASURE THIS OVER SITUATIONS, NOT OVER SEEDS. Twelve seeds all land the harness in nearly the
 * same geometry, because the rule that decides when a carrier has settled fires at the same sort
 * of moment in every match; they agreed to two decimal places and were reporting one situation
 * twelve times. Skipping whole settled spells before the swing is what makes them differ, and
 * on the possession figure the two answers are not close.
 *
 * Only for a person. The computer's carrier is steered by a target that moves slowly enough
 * that the step would hardly ever bind, and changing what it does needs twenty-four matches.
 */
const CARRY_BEND = (22 * Math.PI) / 180;
const CARRY_CUT = (70 * Math.PI) / 180;
const CARRY_HALF_CUT = (45 * Math.PI) / 180;
const CARRY_WHOLE = (80 * Math.PI) / 180;

function bendWhileCarrying(self: MatchPlayer, wanted: Vector3): Vector3 {
  if (!self.hasBestPossession) return wanted;
  const owed = -angle2D(self.directionVec, wanted);
  const size = Math.abs(owed);
  if (size <= CARRY_BEND || size > CARRY_WHOLE) return wanted;
  const step = size <= CARRY_CUT ? CARRY_BEND : CARRY_HALF_CUT;
  return rotate2D(self.directionVec, Math.sign(owed) * step);
}

/** How long after the lunge the referee still counts it as violent. Two thirds of a second. */
const VIOLENT_CHALLENGE_MS = 660;
/**
 * The furthest a flying kick will look for somebody to launch itself at, in the two modes.
 *
 * The clip itself carries him three metres, so in an ordinary match the far end of this is a
 * man he will only just reach. Where anything goes he throws himself from further out; nothing
 * else about the act changes, and the referee is not touched either way.
 */
const FLYING_KICK_RANGE = 4.5;
const FLYING_KICK_RANGE_ANYTHING_GOES = 7.0;

/**
 * The flying kick: its own drawing, aimed at the nearest opponent rather than at the ball.
 *
 * TWO COMMANDS COME BACK AND THE SECOND ONE IS NOT A SPARE. `special/flying_kick` was drawn
 * coming in at 7 m/s, and the hard filter will not start a sprint clip on a man who is not
 * sprinting (`crudeSelection`, case (b): a clip whose own incoming speed is above the player's
 * bucket is thrown out). So the leap is only available to a man with a run-up, which is also
 * the only way a person ever leaves the ground. Behind it sits the lunge this act used to be —
 * a sliding clip pointed at the man — for the press made from a walk. Both are the same
 * offence and the referee is told the same thing about either.
 *
 * A special clip is played EXACTLY AS DRAWN: its physics bias is zero
 * (`physicsBiasFor`, `src/sim/anim/physics.ts:233`), so its own root track is the movement and
 * there is no solver underneath it to bend towards the victim. He goes where he is already
 * pointed. That is why the movement behind it, and the person's own steering before it, are
 * what aim this — the command cannot.
 *
 * With nobody in range it is just a slide, played where the person is pointing, and the
 * referee is told nothing — a man throwing himself at thin air has fouled nobody.
 */
function flyingKickCommands(
  match: Match,
  self: MatchPlayer,
  manual: DesiredMovement,
): PlayerCommand[] {
  const range =
    match.violence === 'anything-goes' ? FLYING_KICK_RANGE_ANYTHING_GOES : FLYING_KICK_RANGE;
  const victim = nearestOpponentWithin(match, self, range, manual.direction);
  if (!victim) return [actionCommand('sliding', manual)];
  self.violentChallengeUntilMs = match.timeMs + VIOLENT_CHALLENGE_MS;
  const at = victim.position.clone().addScaledVector(victim.movement, 0.2).setZ(0);
  const aimed: DesiredMovement = {
    direction: normalizedOr(at.clone().sub(self.position), manual.direction),
    velocity: SPRINT_VELOCITY,
    lookAt: at,
  };
  return [
    {
      // The one `special` function type rather than a type of its own: see the note on
      // `specialVar1` in `src/sim/anim/select.ts`. Both flags false exactly as the
      // celebration's are — with the physics bias at zero neither one can steer the clip, and
      // the look-at flag would switch on a rotational fence in the filter that can only throw
      // away one of the two candidates (the clip and its mirror) for no gain.
      desiredFunctionType: 'special',
      useDesiredMovement: false,
      desiredDirection: aimed.direction.clone(),
      desiredVelocityFloat: SPRINT_VELOCITY,
      useDesiredLookAt: false,
      desiredLookAt: at.clone(),
      specialVar1: SPECIAL_FLYING_KICK,
    },
    actionCommand('sliding', aimed),
  ];
}

/**
 * How close he has to be to land a punch, in the two modes. Arm's length.
 *
 * Shorter than the shirt pull's 1.6 m, which is a hand on a shirt at full stretch plus a
 * stride; this is a fist arriving with something behind it.
 */
const PUNCH_RANGE = 1.4;
const PUNCH_RANGE_ANYTHING_GOES = 2.2;

/**
 * How far a punch knocks a man, in metres, applied on the one tick it lands.
 *
 * Eight centimetres is not a number chosen for feel: it is exactly the cap the collision pass
 * puts on its own separation shove (`SPRINT_VELOCITY * 0.01`, `src/sim/collision.ts`), so a
 * punch moves a man as hard as the hardest shove the engine already gives anybody, and no
 * harder. Where anything goes, twice that.
 *
 * It goes through `offsetPosition` like the shirt pull, so it is not a special case bolted on:
 * it counts towards how unbalanced the engine thinks he is, and everything that already reads
 * that — how much power he can put through the ball, how badly his aim suffers — answers to it
 * for free.
 */
const PUNCH_METRES = 0.08;
const PUNCH_METRES_ANYTHING_GOES = 0.16;

/**
 * Which frame of `special/punch` the fist arrives on.
 *
 * The clip's own keys say so: the right shoulder and elbow are keyed at 0, 30, 38, 46 and 59,
 * and the elbow goes from ninety-five degrees of bend at 30 to five at 38. So 0 to 30 is the
 * wind-up, 30 to 38 is the strike, and the rest is the arm coming back.
 *
 * THE STRIKE IS EIGHT FRAMES BECAUSE A PUNCH IS A SNAP. The first version of this clip spread
 * it over twenty and the arm's fastest moment was then the WIND-UP — measured in the clip
 * viewer, 6.16 m/s at frame 19 against a throw-in's 14.25, which is a man who cocks his arm
 * faster than he hits. Re-authored, the same measurement gives 16.06 m/s and its peak falls
 * inside the strike. Anything that re-times this clip has to move this constant with it.
 */
const PUNCH_STRIKE_FRAME = 38;

/**
 * Is this the one tick on which a punch connects?
 *
 * THE OFFENCE IS TIED TO THE ANIMATION AND NOT TO THE KEY, and the first shape of this was
 * tied to the key, and it was wrong in a way that only showed up in a real match. Gating on
 * "he is standing still and somebody is in reach" is gating on the same conditions the clip
 * needs, so it fires on the tick BEFORE the swing is selected — the referee then blows, play
 * stops, and the next animation decision is taken with the match halted, so the swing never
 * happens at all. Driven live, that produced a red card, a felled opponent and a man who
 * visibly did nothing. Asking the clip instead cannot come apart: what is punished is the
 * punch that was thrown.
 *
 * Exactly one tick, because a clip advances exactly one frame a tick and a `special` is never
 * reconsidered once it is running.
 */
function punchConnects(self: MatchPlayer): boolean {
  return (
    self.anim.currentFunctionType === 'special' &&
    Number(self.anim.clip.vars['specialvar1'] ?? '0') === SPECIAL_PUNCH &&
    self.anim.frameNum === PUNCH_STRIKE_FRAME
  );
}

/**
 * Who a punch would land on, or null.
 *
 * Shared by the command that asks for the swing, the caption that names the key, and the blow
 * itself, so all three agree about who he is swinging at.
 */
export function punchVictim(match: Match, self: MatchPlayer): MatchPlayer | null {
  const range = match.violence === 'anything-goes' ? PUNCH_RANGE_ANYTHING_GOES : PUNCH_RANGE;
  return nearestOpponentWithin(match, self, range, self.directionVec);
}

/**
 * Swing at the nearest man: stand still, and throw one.
 *
 * The stop is the second command and it is load-bearing. `special/punch` was drawn from a
 * standstill, and the hard filter will not start a standing clip on a man who is moving
 * (`crudeSelection`, case (b): a clip whose own incoming speed is idle is thrown out for a
 * walking or sprinting player, exactly as a sprinting clip is thrown out for a standing one).
 * So a punch is thrown from a standstill or not at all — which is what a man does anyway, he
 * pulls up and swings — and a key that does nothing for as long as it takes a person to work
 * out that he has to stop first is a key nobody presses twice. Asking for nought m/s brakes
 * him, and the swing starts on the tick he arrives in the idle bucket.
 */
function punchCommands(match: Match, self: MatchPlayer, manual: DesiredMovement): PlayerCommand[] {
  const victim = punchVictim(match, self);
  const at = (victim?.position ?? self.position.clone().addScaledVector(self.directionVec, 10))
    .clone()
    .setZ(0);
  return [
    {
      desiredFunctionType: 'special',
      useDesiredMovement: false,
      desiredDirection: self.directionVec.clone(),
      desiredVelocityFloat: IDLE_VELOCITY,
      useDesiredLookAt: false,
      desiredLookAt: at,
      specialVar1: SPECIAL_PUNCH,
    },
    movementCommand(manual.direction, IDLE_VELOCITY, manual.lookAt),
  ];
}

/**
 * Throw a body swerve, and carry the ball at dribbling pace while he does.
 *
 * THE SECOND COMMAND IS WHAT MAKES THE FIRST REACHABLE, and it is the punch's lesson again in
 * a different place. `special/feint` was drawn at 3.5 m/s — two steps of the original's own
 * dribbling stride — and the hard filter throws out a clip whose own incoming speed sits in a
 * different bucket from the man's. So a sprinting man cannot start one, and a key that does
 * nothing until a person happens to be running at exactly the right pace is a key nobody
 * presses twice. Asking to carry the ball at dribbling pace slows him with the ball still at
 * his feet, and the swerve starts on the tick he arrives in that bucket.
 *
 * Nothing follows it in the queue. A movement command always selects something, so the ball
 * control below it would never be reached anyway — and a man in the middle of throwing a
 * dummy is not also passing.
 *
 * HE DOES NOT CHOOSE THE SIDE, AND THAT IS FOOTBALL RATHER THAN A LIMITATION. The clip and
 * its mirror are identical to the filter and to every sort above library order except one:
 * which foot each starts on. So the twin matching the foot he is on wins, and he swerves off
 * whichever foot he happens to be running on, which is the only one he could push off anyway.
 */
function feintCommands(
  self: MatchPlayer,
  image: MentalImage,
  movement: DesiredMovement,
): PlayerCommand[] {
  return [
    {
      desiredFunctionType: 'special',
      // Both false, as the celebration's and the flying kick's are: a special clip is played
      // exactly as drawn (`physicsBiasFor`), so there is no solver underneath to bend towards
      // anything, and neither flag can reach a second candidate that the foot has not already
      // decided between.
      useDesiredMovement: false,
      desiredDirection: self.directionVec.clone(),
      desiredVelocityFloat: DRIBBLE_VELOCITY,
      useDesiredLookAt: false,
      desiredLookAt: movement.lookAt.clone(),
      specialVar1: SPECIAL_FEINT,
    },
    ballControlMovement(self, image, DRIBBLE_VELOCITY),
  ];
}

/**
 * Is he in a position to throw one at all?
 *
 * Three things, and the third is the only one that is a judgement. He must have the ball,
 * because a feint is a thing you do with it. He must be under the walking bucket, because
 * above it the clip cannot be selected and the command would only ever brake him. And he must
 * not already be swerving: a `special` is never reconsidered once it starts, so a second
 * request while the first is running is a request to feint again the moment this one ends.
 */
export function canFeint(match: Match, self: MatchPlayer): boolean {
  if (match.ballRetainer || !self.hasBestPossession) return false;
  if (self.movement.length() >= DRIBBLE_WALK_SWITCH) return false;
  return !isFeinting(self);
}

/** Is this man in the middle of a body swerve right now? */
export function isFeinting(self: MatchPlayer): boolean {
  return (
    self.anim.currentFunctionType === 'special' &&
    Number(self.anim.clip.vars['specialvar1'] ?? '0') === SPECIAL_FEINT
  );
}

/**
 * How close the man he is holding off has to be.
 *
 * Two and a half metres is a stride and a half — near enough that he is genuinely on you and
 * that turning into him loses the ball. Shielding an empty pitch is a man standing still with
 * his arms out for no reason, and the whole point of the act is the opponent.
 */
const SHIELD_OPPONENT_RANGE = 2.5;

/**
 * Stand over it, turn your back on him and hold him off.
 *
 * NO CLIP IN THE LIBRARY CARRIES THE BALL WITH THE BODY TURNED — measured, not assumed: of the
 * 45 ball-control clips the most twisted is 2 degrees off the run, where 401 of the 508 plain
 * movement clips turn more than 30 and some reach 139. So a man in possession could only ever
 * face where he was going, and shielding had no drawing at all. `special/shield` is this
 * project's: a wide braced stance, both arms out to 0.40 m against a standing man's 0.23, feet
 * 0.54 m apart against 0.44, and the whole man three centimetres lower, backing 0.12 m into
 * whoever is behind him over seven tenths of a second.
 *
 * WHICH WAY HE FACES IS THE SECOND COMMAND'S DOING, NOT THE FIRST'S. A special clip is played
 * exactly as drawn (`physicsBiasFor`), so nothing about the shield itself can point him: it
 * turns him not at all and holds whatever facing he arrives with. The stop behind it carries a
 * look-at on the far side of him from the opponent, so he turns his back on the man first and
 * plants second — and being a stop, it is also what brakes him into a clip drawn from a
 * standstill, exactly as the punch's does.
 *
 * He does not touch the ball while he does it: a `special` has no ball-contact branch at all.
 * That is why the range test asks for a ball he has actually settled — one still rolling would
 * simply run away from him over the seven tenths, with nothing in the clip able to stop it.
 */
function shieldCommands(match: Match, self: MatchPlayer): {
  commands: PlayerCommand[];
  /** The stop, handed back so the computer's queue can adopt it as what he is asking for. */
  stance: DesiredMovement;
} {
  const opponent = nearestOpponent(match, self, SHIELD_OPPONENT_RANGE);
  const away = normalizedOr(
    self.position.clone().sub(opponent?.position ?? self.position).setZ(0),
    self.directionVec,
  );
  const at = self.position.clone().addScaledVector(away, 10).setZ(0);
  const stance: DesiredMovement = { direction: away.clone(), velocity: IDLE_VELOCITY, lookAt: at.clone() };
  const stop = movementCommand(away, IDLE_VELOCITY, at);

  // AND HE HAS TO HAVE FINISHED TURNING BEFORE HE PLANTS. The queue is tried in order and the
  // shield would otherwise win on the first tick he reaches a standstill, freezing him
  // mid-turn with the man he is holding off somewhere off his shoulder — measured in a rigged
  // chase, 168 degrees out, which is a man shielding the ball from nobody and presenting it to
  // the opponent. So until his back is roughly to him there is only the stop, which is what
  // turns him; the shield joins the queue when the turn is done.
  if (Math.abs(angle2D(self.directionVec, away)) > SHIELD_FACING_TOLERANCE) {
    return { commands: [stop], stance };
  }

  return {
    commands: [
      {
        desiredFunctionType: 'special',
        useDesiredMovement: false,
        desiredDirection: away.clone(),
        desiredVelocityFloat: IDLE_VELOCITY,
        useDesiredLookAt: false,
        desiredLookAt: at.clone(),
        specialVar1: SPECIAL_SHIELD,
      },
      stop,
    ],
    stance,
  };
}

/**
 * How far off "back to the man" he may be and still plant.
 *
 * Fifty degrees is a shoulder rather than a back, which is the other half of how footballers
 * actually shield — side-on, with an arm across. Tighter than this and a man turning on the
 * spot never quite arrives, because an idle turn lands on whichever body angle the library
 * happens to serve rather than on the exact bearing asked for.
 */
const SHIELD_FACING_TOLERANCE = (50 * Math.PI) / 180;

/**
 * Is there anything to shield the ball from?
 *
 * The ball has to be his and it has to be still — a ball he is still knocking ahead of himself
 * is not one he can stand over — and somebody has to be on him. Nothing else: how long he holds
 * it there is his own business, and the clip is re-asked for as long as the key is down.
 */
export function canShield(match: Match, self: MatchPlayer): boolean {
  if (match.ballRetainer || !self.hasBestPossession) return false;
  return nearestOpponent(match, self, SHIELD_OPPONENT_RANGE) !== null;
}

/**
 * The nearest opponent within `radius`, wherever he is standing.
 *
 * Not `nearestOpponentWithin`, which weights a man behind you at one and a half times his real
 * distance because everything that has used it until now was aimed at somebody — a punch, a
 * lunge, a shirt. Shielding is the opposite: the man who matters is precisely the one you
 * cannot see.
 */
function nearestOpponent(match: Match, self: MatchPlayer, radius: number): MatchPlayer | null {
  let best: MatchPlayer | null = null;
  let bestDistance = radius;
  for (const opponent of match.opponentTeam(self.teamId).players) {
    if (!opponent.active) continue;
    const distance = Math.hypot(
      opponent.position.x - self.position.x,
      opponent.position.y - self.position.y,
    );
    if (distance < bestDistance) {
      bestDistance = distance;
      best = opponent;
    }
  }
  return best;
}

/**
 * WHEN THE COMPUTER THROWS A BODY SWERVE, and every one of these numbers was measured rather
 * than chosen.
 *
 * The situation itself is far commoner than the act should be, which is the whole reason there
 * is a gate here at all. Over four matches the man with the ball was at dribbling pace with
 * somebody inside three metres of his running line for 2,186 ticks a match, in 127 separate
 * spells — a body swerve every twelve seconds of football, where a real match has perhaps
 * thirty-five attempted take-ons in the whole ninety minutes. So the grid below was run over
 * the same four matches, counting how many swerves each candidate threshold would actually
 * produce with a four-second cooldown behind it (`.agent/scratch/acts-grid.ts`):
 *
 *              inside 30 deg   45 deg   60 deg
 *     1.5 m         45.0        54.5     63.0
 *     2.0 m         54.0        65.8     77.0
 *     2.5 m         61.0        74.3     88.0
 *     3.0 m         67.8        83.8     98.8
 *
 * and with the closing test as well: 60.0 at 2.0 m, 68.5 at 2.5, 77.5 at 3.0.
 *
 * Two metres is a stride and a half — the man is genuinely on him — and forty-five degrees is
 * "in his way" rather than "directly in front", which is what a defender coming to meet a
 * runner looks like. THE CLOSING TEST IS THE ONE THAT MEANS SOMETHING FOOTBALLING: you do not
 * swerve past a man who is backing off, you simply run at him. Half a metre a second is a walk.
 */
const SWERVE_OPPONENT_RANGE = 2.0;
const SWERVE_CONE = (45 * Math.PI) / 180;
const SWERVE_CLOSING_SPEED = 0.5;

/**
 * How long before he may throw another, on the match's own clock.
 *
 * Four seconds, and the floor under it is the clip: a swerve is 49 frames, so anything under
 * half a second would let him chain them into a pirouette. The gate above holds for seventeen
 * ticks at a time on average, so what this number really sets is how many of a single
 * seventeen-second passage of pressure become separate swerves — at four seconds, at most four.
 */
const SWERVE_COOLDOWN_MS = 4000;

/**
 * The computer's own body swerve.
 *
 * BELOW THE PASS AND THE SHOT IN HIS QUEUE, deliberately: a man with a team-mate better placed
 * plays the ball to him, and it is the man with nowhere to put it who beats his opponent
 * himself. That is football, and it is also what keeps this from taking chances away from
 * anybody — a swerve can only ever be reached on a tick where the passing and shooting
 * decisions had already declined to produce a command.
 *
 * Only the special is issued, where a person's press also queues a ball control at dribbling
 * pace behind it. The difference is what each is for: the press has to BRAKE him into the
 * bucket the clip lives in, because a person may press it at a sprint, whereas this asks only
 * of a man already travelling at dribbling pace. So a swerve that cannot be selected here
 * falls straight through to the ordinary queue and he carries on as though nothing had been
 * asked, instead of being slowed for a clip he was never going to get.
 */
function computerSwerveCommand(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
  movement: DesiredMovement,
): PlayerCommand | null {
  if (match.timeMs < self.swerveAllowedFromMs) return null;
  if (!canFeint(match, self)) return null;
  // The clip was drawn coming in at 3.5 m/s, so it lives in the dribbling bucket and nowhere
  // else. `canFeint` rules out the bucket above; this rules out the one below, where a man
  // barely moving would spend a cooldown on a clip the filter was never going to give him.
  const speed = self.movement.length();
  if (speed < IDLE_DRIBBLE_SWITCH) return null;
  if (!closingOpponentAhead(match, self)) return null;

  self.swerveAllowedFromMs = match.timeMs + SWERVE_COOLDOWN_MS;
  return feintCommands(self, image, movement)[0]!;
}

/** Somebody inside a stride and a half of his path, coming towards him. */
function closingOpponentAhead(match: Match, self: MatchPlayer): boolean {
  const toMan = new Vector3();
  for (const opponent of match.opponentTeam(self.teamId).players) {
    if (!opponent.active) continue;
    toMan.copy(opponent.position).sub(self.position).setZ(0);
    const distance = toMan.length();
    if (distance > SWERVE_OPPONENT_RANGE || distance < 1e-6) continue;
    if (Math.abs(angle2D(self.directionVec, toMan)) > SWERVE_CONE) continue;
    // The speed the gap between them is shrinking at, which is the closing speed along the
    // line between the two men rather than either man's own pace.
    const closing =
      -(opponent.movement.x - self.movement.x) * (toMan.x / distance) -
      (opponent.movement.y - self.movement.y) * (toMan.y / distance);
    if (closing > SWERVE_CLOSING_SPEED) return true;
  }
  return false;
}

/**
 * WHEN THE COMPUTER STANDS OVER THE BALL, and again the numbers come off the same four matches.
 *
 * A man with somebody within two and a half metres behind him has the ball 12,544 ticks a match
 * in 270 separate spells, so the raw situation would produce a shield every five seconds. The
 * grid, in shields a match with a four-second cooldown:
 *
 *              over 90 deg   120 deg   135 deg
 *     1.2 m        115.8       99.8      88.8
 *     1.5 m        137.3      120.0     109.0
 *     2.0 m        158.3      138.3     127.0
 *     2.5 m        174.5      154.3     142.5
 *
 * and with "he has already slowed to a standstill" added: 30.0 at 1.5 m, 39.5 at 2.0, 45.5 at
 * 2.5 — a tenth of the rest, and by far the most important of the three tests.
 *
 * THAT IS BECAUSE IT IS THE ONLY ONE THAT DESCRIBES THE ACT RATHER THAN THE SCENERY. Shielding
 * is what a man does when he has run out of pitch, not what he does while running; asking it of
 * somebody at pace would have him brake to a halt to plant, which throws away the very thing he
 * was doing. A hundred and twenty degrees is behind his shoulder, and a metre and a half is
 * close enough that turning into the man loses the ball.
 */
const COMPUTER_SHIELD_RANGE = 1.5;
const COMPUTER_SHIELD_ARC = (120 * Math.PI) / 180;

/**
 * How long he holds it there, and how long before he may do it again.
 *
 * A shield is 71 frames, so 1.2 seconds is the turn plus one clip and a little of a second —
 * long enough for a team-mate to come and take the ball off him, short enough that nobody
 * stands on it. The bound matters more than it looks: he does not touch the ball while he
 * shields, so with no ceiling on it two men could stand over a motionless ball indefinitely,
 * which is the frozen-football failure `dribbleMovement`'s speed floor exists to prevent.
 */
const SHIELD_HOLD_MS = 1200;
const SHIELD_COOLDOWN_MS = 4000;

/**
 * The computer's own shield — the same act a person gets, on a judgement instead of a key.
 *
 * It sits in the queue exactly where the swerve does, under the pass and the shot, and for the
 * same reason: a man who can give the ball to somebody gives it to him. What is left underneath
 * is a man who has stopped, with an opponent at his back and nothing on, and standing over it is
 * a better answer than the jog in whatever direction he is facing that the dribbling floor would
 * otherwise give him.
 */
function computerShieldCommands(
  match: Match,
  self: MatchPlayer,
): { commands: PlayerCommand[]; stance: DesiredMovement } | null {
  // Already in one: it runs on its own clock, and ends early only if the reason for it goes —
  // the ball is no longer his, or the man has come off his back.
  if (match.timeMs < self.shieldingUntilMs) {
    return canShield(match, self) ? shieldCommands(match, self) : null;
  }
  if (match.timeMs < self.shieldAllowedFromMs) return null;
  if (!canShield(match, self)) return null;
  if (self.movement.length() >= IDLE_DRIBBLE_SWITCH) return null;
  const behind = nearestOpponent(match, self, COMPUTER_SHIELD_RANGE);
  if (!behind) return null;
  const toMan = behind.position.clone().sub(self.position).setZ(0);
  if (Math.abs(angle2D(self.directionVec, toMan)) < COMPUTER_SHIELD_ARC) return null;

  self.shieldingUntilMs = match.timeMs + SHIELD_HOLD_MS;
  self.shieldAllowedFromMs = self.shieldingUntilMs + SHIELD_COOLDOWN_MS;
  return shieldCommands(match, self);
}

/**
 * Land the punch: knock him back, and tell the referee it was a straight red.
 *
 * The referee has to be told, exactly as he does for a shirt pulled: everything he punishes in
 * the original comes out of two men's legs overlapping, and there is no challenge in this at
 * all. Type 3 is his straight red (`Referee.offenceNotice`), and he takes one offence at a
 * time, so holding the key down is one punch rather than a hundred.
 *
 * The shove is BACKWARDS ALONG THE LINE BETWEEN THEM, which is the direction a fist travels.
 * That differs from the shirt pull deliberately: a shirt does not throw you anywhere, it stops
 * you going, so that one retards the victim along his own run instead.
 *
 * He also goes down, and he goes down STRUCK rather than tripped. The trip family is a man
 * whose legs met another man's legs, which is not what being punched looks like; the clips
 * drawn for this are `special/struck_standing` and `special/struck_running`, and `struck`
 * picks whichever suits the speed he was going.
 *
 * AND HE CAN BE HURT BY IT, which needs saying because it took a hand-wiring the collision
 * pass does for free. A punch has no collision in it — that is the whole reason the referee
 * has to be told directly — so nothing was ever going to reach `assessInjury` on its behalf.
 * The mark that says this was violent rather than an honest challenge is the same one the
 * flying kick sets and the referee reads.
 */
export function applyPunch(match: Match, puncher: MatchPlayer): MatchPlayer | null {
  if (!punchConnects(puncher)) return null;
  const victim = punchVictim(match, puncher);
  if (!victim) return null;
  const metres =
    match.violence === 'anything-goes' ? PUNCH_METRES_ANYTHING_GOES : PUNCH_METRES;
  const away = normalizedOrZero(victim.position.clone().sub(puncher.position).setZ(0));
  if (away.lengthSq() > 0) {
    victim.anim.offsetPosition?.(away.clone().multiplyScalar(metres));
    victim.anim.struck?.(away);
  }
  puncher.violentChallengeUntilMs = match.timeMs + VIOLENT_CHALLENGE_MS;
  match.assessInjury(victim, puncher, 3);
  match.referee.offenceNotice(match, victim, puncher, 3);
  return victim;
}

/**
 * The nearest opponent inside a radius, preferring the way the person is pointing.
 *
 * Shared by the flying kick and the shirt pull, and both want the same thing: the man he
 * plainly means, not whoever happens to be a centimetre closer behind his back. A candidate
 * behind him is allowed but is treated as if he were half again as far away.
 */
export function nearestOpponentWithin(
  match: Match,
  self: MatchPlayer,
  radius: number,
  facing: Vector3,
): MatchPlayer | null {
  let best: MatchPlayer | null = null;
  let bestScore = radius;
  for (const opponent of match.opponentTeam(self.teamId).players) {
    if (!opponent.active) continue;
    const offset = opponent.position.clone().sub(self.position).setZ(0);
    const distance = offset.length();
    if (distance > radius) continue;
    const ahead = normalizedOr(offset, facing).dot(facing) > 0;
    const score = ahead ? distance : distance * 1.5;
    if (score < bestScore) {
      bestScore = score;
      best = opponent;
    }
  }
  return best;
}

function humanTouchMovement(
  self: MatchPlayer,
  manual: DesiredMovement,
  actionBuffered: boolean,
): DesiredMovement {
  if (!actionBuffered) return manual;
  const dot = Math.pow(self.directionVec.dot(manual.direction) * 0.5 + 0.5, 1.5) * 0.8 + 0.2;
  return {
    direction: self.floatVelocity > 0 ? self.directionVec : manual.direction,
    velocity: self.floatVelocity * dot * 0.9,
    lookAt: manual.lookAt,
  };
}

/**
 * How long the shot key must be held, read off the power it has charged to, before a hold
 * counts as "prolonged" for the unmarked-shot bonus below.
 *
 * `HumanInputBuffer.currentAction` (`src/input/player.ts`) turns the charge gauge into
 * `power = gauge^0.6`, and gauge itself only reaches exactly 1 once the gauge has sat at its
 * own 1000 ms cap for a tick, so this is a shot held very close to the full second rather than
 * a firm tap. Chosen, not measured.
 */
const SHOT_BONUS_MIN_POWER = 0.97;

/**
 * How close an opponent may be before he counts as marking the shooter closely enough to deny
 * him the bonus below.
 *
 * Chosen, not measured; the same order of magnitude as `BICYCLE_PRESSURE_M` below, the only
 * other distance in this file standing for "somebody is right on him" rather than "somebody
 * happens to be on the pitch".
 */
const SHOT_BONUS_UNMARKED_RADIUS_M = 4.0;

/**
 * How much easier `shotVector` treats a shot that has earned the bonus below, fed straight
 * through as its `confidenceBonus`: the power ceiling rises by this fraction and the difficulty
 * the shot already had is cut by the same fraction.
 *
 * Chosen, not measured, and deliberately modest — this nudges the shot formula that is already
 * tuned against every other one in the game, it does not replace it.
 */
const SHOT_BONUS_CONFIDENCE = 0.2;

/**
 * Whether a shot has earned the "nobody is closing him down" bonus: the charge key has been
 * held almost to its cap, and no opponent is close enough to seriously contest the strike.
 *
 * Human only — the computer's own `shotCommand` never calls this, so its shots are untouched.
 *
 * `nearestOpponentWithin` is the same proximity test the shirt-pull and the flying kick already
 * use for "is there a man right here", preferring an opponent the shooter is facing — which is
 * also the direction a defender needs to be in to press or block a shot, so the same bias fits
 * here for the same reason. Two other measures in this file were weighed and set aside:
 * `freeSpace` averages crowding from every opponent near a point projected several seconds into
 * the future, which is the right question for rating a pass but the wrong one for "is somebody
 * about to get a boot to this strike right now"; `duelLikeliness` asks whether a defender is
 * closing on the ball, from that defender's own side of a tackle decision, not whether anyone
 * is within reach of this shooter.
 */
function unmarkedMaxChargeShot(
  match: Match,
  self: MatchPlayer,
  action: HumanActionIntent | null,
): boolean {
  return (
    action !== null &&
    action.type === 'shot' &&
    action.power >= SHOT_BONUS_MIN_POWER &&
    !nearestOpponentWithin(match, self, SHOT_BONUS_UNMARKED_RADIUS_M, self.directionVec)
  );
}

function humanStrikeCommand(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
  intent: HumanPlayerIntent,
  action: Exclude<HumanActionIntent, { type: 'sliding' | 'flyingkick' | 'punch' | 'feint' }>,
): PlayerCommand {
  const inputDirection = normalizedOr(intent.direction, self.directionVec);
  if (action.type === 'shot') {
    const goalDirection = normalizedOr(
      new Vector3(-match.team(self.teamId).side * PITCH.halfLength, 0, 0).sub(self.position),
      inputDirection,
    );
    // Open play keeps assist = keyboard ? 1 : 0.2 exactly as it was: a keyboard's eight
    // directions cannot place a snap shot precisely with a defender closing him down, so it is
    // steered at goal instead and the arrows contribute nothing — deliberate for a moving man
    // under pressure. A DEAD BALL IS THE OPPOSITE SITUATION: nobody is closing him down, the
    // football clock is not even running, and he has as long as he likes to line it up. So at a
    // restart (`match.inSetPiece`) the assist drops to zero for every device and the arrows are
    // the whole of the aim. This is the one behavioural difference `inSetPiece` makes anywhere in
    // this function; open-play shot aiming — already separately tuned — is untouched.
    const assist = match.inSetPiece ? 0 : intent.keyboard ? 1 : 0.2;
    const direction = normalizedOr(
      inputDirection.clone().multiplyScalar(1 - assist).addScaledVector(goalDirection, assist),
      goalDirection,
    );
    // Prolonged hold, nobody marking him: he strikes it harder and truer than an ordinary
    // charged shot, because he is genuinely unhurried rather than merely fully charged.
    const confidenceBonus = unmarkedMaxChargeShot(match, self, action) ? SHOT_BONUS_CONFIDENCE : 0;
    return strikeCommand('shot', {
      direction: inputDirection,
      velocity: intent.velocity,
      lookAt: self.position.clone().addScaledVector(direction, 10),
    }, direction, action.power, confidenceBonus);
  }

  const passType = action.type;
  // Same story as the shot above: in open play, both the direction and the distance are diluted
  // by an auto-aim at whichever teammate `passingOdds` likes best, because a passer under
  // pressure benefits from the help. At a restart nothing is closing him down either, so both
  // assists drop to zero and the arrows plus the charge are the whole of it — the `best` search
  // below still runs (it is cheap, and keeping one formula rather than two keeps this function
  // honest about what changes), its result is simply not blended in when both assists are zero.
  const assist = match.inSetPiece ? 0 : passType === 'shortpass' ? 0.4 : 0.2;
  // How much of a pass's power comes from the auto-picked target's real distance rather than
  // from how long the button was held. This ran 0.5 for the cross and 0.7 for the other two --
  // MORE auto-driven for a five-yard pass than for a cross -- so even the lightest tap already
  // inherited half or more of its power from wherever the best target stood. Measured: tapping
  // the cross key with a striker 39 m away sent the ball out at ~20 m/s, a firmly struck ball
  // rather than the weak nudge a tap should give, and holding it a full second only reached
  // ~42 m/s. Now 0.15 for all three in open play, so the charge is what mostly decides how hard
  // the ball goes and the auto target keeps just enough pull that a held pass still paces toward
  // him; at a restart it drops to zero like the assist above, for the same reason.
  const powerAssist = match.inSetPiece ? 0 : 0.15;
  let best:
    | {
        direction: Vector3;
        distance: number;
        rating: number;
      }
    | null = null;
  for (const teammate of match.team(self.teamId).players) {
    if (teammate === self || !teammate.active) continue;
    const result = passingOdds(match, self, teammate, image, passType);
    const direction = normalizedOr(result.target.clone().sub(self.position), inputDirection);
    const alignment = direction.dot(inputDirection);
    const rating = result.odds * 0.35 + alignment * 0.65;
    if (!best || rating > best.rating) {
      best = {
        direction,
        distance: result.target.distanceTo(self.position),
        rating,
      };
    }
  }
  const targetDirection = best?.direction ?? inputDirection;
  const direction = normalizedOr(
    inputDirection.clone().multiplyScalar(1 - assist).addScaledVector(targetDirection, assist),
    inputDirection,
  );
  // How far he is asking it to go, not how fast. The original turns the charge on the key into
  // a DISTANCE — `manualTarget = playerPos + inputDirection * clamp(inputPower * 60, 1, 100)`
  // (`AIfunctions.cpp:1131`) — and then reads both the elevation and the power off that, which
  // is why a cross rises and a five-yard ball does not. Reading the power off the key directly,
  // as this used to, cannot produce a height at all.
  const manualDistance = clamp(action.power * 60, 1, 100);
  const distance = manualDistance * (1 - powerAssist) + (best?.distance ?? manualDistance) * powerAssist;
  const aim = autoPass(direction.clone().multiplyScalar(distance), passType === 'highpass');
  return strikeCommand(passType, {
    direction: inputDirection,
    velocity: intent.velocity,
    lookAt: self.position.clone().addScaledVector(direction, 10),
  }, aim.direction, aim.power);
}

/**
 * Walk in on the ball to control it — `AI_GetBallControlMovement` (`AIfunctions.cpp:893-939`).
 *
 * Head for where the ball will be a third of a second from now, at a speed proportional to how
 * far away that is, and look where you are going. Two details are the original's and both
 * matter. Within 40 cm the heading stops pointing at the ball and fades back to the way the
 * player is already facing, because at that range the direction to the ball swings wildly from
 * tick to tick and a player who tracked it would jitter on the spot. And the resulting speed is
 * pulled most of the way onto whichever of the four animated speeds is nearest, but only once
 * it is above a slow dribble — below that the original leaves it alone.
 */
function ballControlMovement(
  self: MatchPlayer,
  image: MentalImage,
  desiredVelocity: number,
): PlayerCommand {
  const toBall = image.ballPrediction(250 + DEFAULT_TOUCH_OFFSET_MS).setZ(0).sub(self.position);
  const distance = toBall.length();
  const autoBias = distance < 0.4 ? Math.sqrt(normalizedClamp(distance, 0.2, 0.4)) : 1;
  const direction = normalizedOr(
    normalizedOr(toBall, self.directionVec)
      .multiplyScalar(autoBias)
      .addScaledVector(self.directionVec, 1 - autoBias),
    self.directionVec,
  );

  let velocity = distance * 2.6;
  if (velocity >= DRIBBLE_VELOCITY) {
    velocity = clamp(desiredVelocity, velocity, velocity + 8);
    const ranged = rangeVelocity(velocity);
    if (ranged < velocity) velocity = velocity * 0.9 + ranged * 0.1;
    else if (ranged > velocity) velocity = velocity * 0.1 + ranged * 0.9;
  }

  return movementCommand(direction, velocity, self.position.clone().addScaledVector(direction, 10));
}

/**
 * The set-piece taker, `elizacontroller.cpp:135-241`.
 *
 * He always gets two commands, and the second one is not optional. The first is the kick
 * itself; the second walks him onto the ball. He is placed a couple of metres behind it on
 * purpose, as a run-up, and the kick alone will not carry him there — so without the walk he
 * stands over an untaken free kick for ever, the clock stops, and the match never ends. That
 * is a hang, not a slow restart: it was reproduced running at eight times speed, thirty-four
 * simulated minutes with the football clock frozen at 39 seconds and the taker two point three
 * metres from a motionless ball.
 */
function setPieceCommands(match: Match, self: MatchPlayer): PlayerCommand[] {
  const team = match.team(self.teamId);
  const walkOntoBall = () =>
    ballControlMovement(self, match.mentalImageFor(self), WALK_VELOCITY);

  if (match.setPiece === SetPiece.Penalty) {
    const target = new Vector3(
      -team.side * PITCH.halfLength,
      deterministicRandom(match, self.id, 9) * 10 - 5,
      0,
    );
    const movement: DesiredMovement = {
      direction: self.directionVec,
      velocity: 0,
      lookAt: target,
    };
    return [
      strikeCommand(
        'shot',
        movement,
        target.clone().sub(self.position),
        0.4 + deterministicRandom(match, self.id, 10) * 0.6,
      ),
      walkOntoBall(),
    ];
  }
  const teammates = team.players.filter((player) => player !== self);
  let high = false;
  let desiredTarget = self.position;
  if (match.setPiece === SetPiece.FreeKick) {
    high = deterministicRandom(match, self.id, 7) < 0.5;
    const lateral = deterministicRandom(match, self.id, 8) * 20 - 10;
    desiredTarget = high
      ? new Vector3(-team.side * PITCH.halfLength, lateral, 0)
      : self.position.clone().add(new Vector3(-team.side * 10, lateral, 0));
  } else if (match.setPiece === SetPiece.GoalKick) {
    const noHumanOnSide = !team.players.some((player) => match.humanControlled(player));
    high = noHumanOnSide && deterministicRandom(match, self.id, 12) < 0.6;
    const lateral = deterministicRandom(match, self.id, 13) * (2 * PITCH.halfWidth) - PITCH.halfWidth;
    desiredTarget = high
      ? new Vector3(-team.side * PITCH.halfLength * 0.2, lateral, 0)
      : new Vector3(self.position.x * 0.9, lateral, 0);
  } else if (match.setPiece === SetPiece.Corner) {
    high = deterministicRandom(match, self.id, 14) < 0.7;
    desiredTarget = high
      ? new Vector3(
          -team.side * PITCH.halfLength * (0.99 - deterministicRandom(match, self.id, 15) * 0.12),
          deterministicRandom(match, self.id, 16) * 20 - 10,
          0,
        )
      : new Vector3(-team.side * PITCH.halfLength * 0.8, self.position.y * 0.8, 0);
  }
  const target = teammates.reduce(
    (closest, player) =>
      player.position.distanceToSquared(desiredTarget) < closest.position.distanceToSquared(desiredTarget)
        ? player
        : closest,
    teammates[0] ?? self,
  );
  return [forcedPass(self, target.position, high).command, walkOntoBall()];
}

/**
 * `AI_GetAutoPass` (`AIfunctions.cpp:1093-1106`): turn "play it over there" into the direction
 * and the power of the contact.
 *
 * THE LIFT IS THE WHOLE POINT OF IT, AND UNTIL NOW NOTHING IN THIS GAME EVER LEFT THE GROUND.
 * The contact is `direction * 36 * (power + 0.3)` (`humanoid.cpp:509`) and that direction is
 * the only place a pass gets any height at all, so a direction with no upward component is a
 * pass that rolls. A high pass is tilted up by between 0.30 and 0.45 before it is normalised —
 * seventeen to twenty-four degrees, which is a cross — and even an ordinary pass gets 0.11, a
 * ball that comes off the turf rather than hugging it.
 *
 * Three of the four places that build a pass had no elevation at all, and the fourth had it
 * written the wrong way round: it set the height on the UNNORMALISED offset to the target, so
 * a thirty-metre ball got 0.45 metres of rise over thirty of run — less than a degree. Measured
 * with a person holding the cross key on the wing: the ball left his foot at 16.6 m/s and 11 cm
 * off the deck, which is a drilled ground pass. There were no crosses in this game.
 */
function autoPass(toTarget: Vector3, high: boolean): { direction: Vector3; power: number } {
  const distance = toTarget.length();
  const heightOffset = high ? 0.45 - normalizedClamp(distance, 0, 60) * 0.15 : 0.11;
  // Normalise FIRST, then add the height, then normalise again. The order is what was wrong.
  const direction = normalizedOr(toTarget.clone().setZ(0), FORWARD);
  direction.z = heightOffset;
  direction.normalize();
  const power = Math.pow(normalizedClamp(distance, 0, 60), 1.4) * (high ? 1.15 : 1.8);
  return { direction, power };
}

/**
 * `AI_GetPass` once the receiver has already been chosen: aim at him and read the power off
 * the distance. Shared by every set piece and by the keeper throwing the ball back into play.
 */
function forcedPass(
  self: MatchPlayer,
  target: Vector3,
  high: boolean,
): { command: PlayerCommand; direction: Vector3 } {
  const { direction, power } = autoPass(target.clone().sub(self.position), high);
  const movement: DesiredMovement = {
    direction: self.directionVec,
    velocity: 0,
    lookAt: target.clone(),
  };
  return {
    command: strikeCommand(high ? 'highpass' : 'shortpass', movement, direction, power),
    direction,
  };
}

/** `AI_GetClosestPlayer` (`AIfunctions.cpp:974`): nearest active man to a point, one excluded. */
function closestPlayer(
  players: MatchPlayer[],
  position: Vector3,
  except: MatchPlayer | null,
): MatchPlayer | null {
  let best: MatchPlayer | null = null;
  let bestDistance = 10000;
  for (const player of players) {
    if (!player.active || player === except) continue;
    const distance = player.position.distanceTo(position);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = player;
    }
  }
  return best;
}

/**
 * A keeper who has the ball in his hands, `elizacontroller.cpp:198-224,235-247`.
 *
 * He picks a spot at random along his own goal line and looks for the team-mate nearest it —
 * in practice one of his defenders — but he will not release the ball while that man has an
 * opponent within ten metres of him. The spot is re-rolled every time he is asked, so he keeps
 * trying different targets until one of them is free. After four seconds of holding he throws
 * it regardless, which is what stops him standing there for the rest of the half.
 *
 * Either way he also gets a movement command that keeps him still and turns him towards
 * whatever he is aiming at, or towards the middle of the pitch while he is still looking.
 */
function retainerCommands(match: Match, self: MatchPlayer): PlayerCommand[] {
  const team = match.team(self.teamId);
  const queue: PlayerCommand[] = [];
  let facing: Vector3 | null = null;

  const spot = new Vector3(
    PITCH.halfLength * team.side,
    (deterministicRandom(match, self.id, 11) * 2 - 1) * PITCH.halfWidth,
    0,
  );
  const receiver = closestPlayer(team.players, spot, self);
  if (receiver) {
    const marker = closestPlayer(match.opponentTeam(self.teamId).players, receiver.position, null);
    const covered =
      marker !== null &&
      marker.position.clone().addScaledVector(marker.movement, 0.1).distanceTo(receiver.position) <= 10;
    if (!covered || self.possessionDurationMs > 4000) {
      const pass = forcedPass(self, receiver.position, true);
      queue.push(pass.command);
      facing = pass.direction;
    }
  }

  const direction = normalizedOr(
    facing ?? new Vector3().sub(self.position).setZ(0),
    self.directionVec,
  );
  queue.push(
    movementCommand(direction, IDLE_VELOCITY, self.position.clone().addScaledVector(direction, 10)),
  );
  return queue;
}

interface DesiredMovement {
  direction: Vector3;
  velocity: number;
  lookAt: Vector3;
}

function movementCommand(
  direction: Vector3,
  velocity: number,
  lookAt: Vector3,
): PlayerCommand {
  const normalized = normalizedOr(direction, FORWARD);
  return {
    desiredFunctionType: 'movement',
    useDesiredMovement: true,
    desiredDirection: normalized,
    desiredVelocityFloat: clamp(velocity, 0, SPRINT_VELOCITY),
    useDesiredLookAt: true,
    desiredLookAt: lookAt.clone().setZ(0),
  };
}

/**
 * How far a player will set off to close down the opponent carrying the ball.
 *
 * `elizacontroller.cpp:343-347`. Three things shorten it: an attacking mindset, having run
 * hard over the last ten seconds, and a low difficulty setting. At the middle difficulty a
 * fresh defender who has been walking will come from about eighteen metres; a forward who
 * has been sprinting, from about five.
 */
/**
 * Whether this player is the one a team-pressure order picked out, and the order still stands.
 * `elizacontroller.cpp:308-313`: never the keeper, never the man the match already thinks is on
 * the ball, and never while his own side has the better claim to it.
 */
export function underTeamPressureOrder(match: Match, self: MatchPlayer): boolean {
  const team = match.team(self.teamId);
  return (
    team.teamPressurePlayer === self &&
    match.timeMs < team.teamPressureUntilMs &&
    match.inPlay &&
    !match.inSetPiece &&
    match.bestPossessionTeamId !== self.teamId &&
    match.designatedPossessionPlayer !== self &&
    self.role !== 'GK'
  );
}

export function huntDistanceThreshold(match: Match, self: MatchPlayer): number {
  const mindset = ROLE_OFFENSIVENESS[self.dynamicRole];
  let threshold = 10 + (1 - mindset) * 10;
  threshold *=
    0.5 * self.fatigueFactorInv +
    0.5 *
      (1 -
        normalizedClamp(self.averageVelocity(VELOCITY_HISTORY_SECONDS), IDLE_VELOCITY, SPRINT_VELOCITY));
  threshold *= 0.3 + match.difficulty * 0.7;
  if (underAllOutOrder(match, self)) threshold *= ALL_OUT_HUNT_FACTOR;
  return threshold;
}

/**
 * Is this man's side under the all-out order this tick, and is he one of the ten it reaches?
 *
 * THREE THINGS IT CAN NEVER DO, and each is a line here rather than a hope.
 *
 * It can never reach the opposing side: the only thing that sets the clock is a person's own
 * key, with his own side's id (`applyAllOut`, `src/input/player.ts`), and the field starts at
 * -1 while the match clock starts at 0. So in a match nobody is playing this returns false on
 * every tick for every player, which is what makes a computer-against-computer fixture
 * byte-identical with and without the whole of this.
 *
 * It can never reach the man the person is driving: his commands are his own, and having the
 * order also loosen the one man whose tackles he is choosing himself would take the decision
 * off him. `Match.humanControlled` is the only thing in the simulation that knows a person is
 * there at all.
 *
 * And it can never reach the goalkeeper, who has his own behaviour entirely and whose idea of
 * going in harder would be to leave his goal.
 */
export function underAllOutOrder(match: Match, self: MatchPlayer): boolean {
  return (
    match.timeMs < match.team(self.teamId).allOutUntilMs &&
    self.role !== 'GK' &&
    !match.humanControlled(self)
  );
}

/**
 * What "harder" is worth, and the three numbers are the whole of the order.
 *
 * CHOSEN, NOT MEASURED, and deliberately modest: each one moves an existing threshold by
 * roughly half its own headroom rather than removing it, because the two tackle gates are the
 * things standing between this game and a free kick every ten seconds. They are in the units of
 * the thresholds they change, so they can be read against them:
 *
 *   hunt      how far a man will leave his shape to close somebody down, ×1.6. At the middle
 *             difficulty that takes a fresh defender who has been walking from about eighteen
 *             metres to about twenty-nine.
 *   interfere how much of a duel it has to look like before he sticks a leg in, 0.2 down to
 *             0.05 — nearly always, rather than only when he is squarely between man and ball.
 *   sliding   the two gates that decide a slide: how much of a duel it has to look like, 0.7
 *             down to 0.5, and how far ahead of the ball he must NOT already be, 0.6 up to 0.9.
 *             The second is the one that bites: it is the "I am losing this race" test, and
 *             raising it is exactly what a man who has stopped caring about the consequences
 *             does.
 */
const ALL_OUT_HUNT_FACTOR = 1.6;
const ALL_OUT_INTERFERE_DUEL = 0.05;
const ALL_OUT_SLIDING_DUEL = 0.5;
const ALL_OUT_SLIDING_POSSESSION = 0.9;

/**
 * Where to stand to cut an opponent off, `playercontroller.cpp:136-179`.
 *
 * The point on his line to our goal that is as far from him as it is from us — the place we
 * and he would arrive at together — nudged a little further along that line to account for
 * the moment it takes to turn and for the ground he covers meanwhile.
 */
function defendPosition(self: MatchPlayer, opponent: MentalImagePlayer, side: number): Vector3 {
  const goal = new Vector3(PITCH.halfLength * side, 0, 0);
  const oppToGoal = goal.clone().sub(opponent.position);
  const oppToGoalLength = oppToGoal.length();

  // The perpendicular bisector of the line between us meets his line to goal at the point
  // equidistant from both; expressed as a fraction along his line, that is this.
  const midpoint = self.position.clone().add(opponent.position).multiplyScalar(0.5);
  const toMid = midpoint.clone().sub(opponent.position);
  const denominator = oppToGoalLength === 0 ? 0 : oppToGoal.dot(toMid) / oppToGoalLength;
  const fraction = oppToGoalLength === 0 ? 0 : clamp(denominator / oppToGoalLength, 0, 1);

  const target = opponent.position.clone().addScaledVector(oppToGoal, fraction);
  const toGoalDirection = normalizedOr(oppToGoal, new Vector3(side, 0, 0));
  target.addScaledVector(toGoalDirection, SPRINT_VELOCITY * 0.1);
  target.addScaledVector(opponent.movement, 0.14);
  return target;
}

/**
 * Whether closing down is worth leaving position for, `humanoid_utils.cpp:117-126`.
 *
 * Only chase when the target is more to the side of you than it is behind you: an opponent
 * who is simply further up the pitch will be met by the man ahead, and running at him just
 * pulls the shape apart.
 */
function needDefendingMovement(side: number, position: Vector3, target: Vector3): boolean {
  const depth = Math.max((target.x - position.x) * -side, 0) - 0.5;
  return Math.abs(target.y - position.y) > depth * 0.8;
}

/**
 * The one AI behaviour that takes the ball off somebody, `elizacontroller.cpp:336-402`.
 *
 * Without it a side keeps its defensive shape perfectly and never actually challenges: the
 * formation settles about eleven metres off a stationary ball carrier and holds there
 * indefinitely, which is exactly what happened before this existed. The two men nearest the
 * opponent on the ball leave their positions and go and get it, and only those two — the
 * rest keep the shape.
 */
export function huntMovement(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
  fallback: DesiredMovement,
): { movement: DesiredMovement; extraHaste: boolean } | null {
  if (match.bestPossessionTeamId === self.teamId) return null;
  if (self.manMarkingId !== -1) return null;

  const opponent = match.opponentTeam(self.teamId).designatedPlayer;
  const opponentImage = image.player(opponent.id);
  if (!opponentImage) return null;

  const opponentSoon = opponentImage.position.clone().addScaledVector(opponentImage.movement, 0.12);
  const meSoon = self.position.clone().addScaledVector(self.movement, 0.04);
  if (opponentSoon.distanceTo(meSoon) >= huntDistanceThreshold(match, self)) return null;

  const team = match.team(self.teamId);
  // The designated man does not abandon a challenge he is already close to winning.
  const extraHaste =
    team.designatedPlayer === self && playerPossessionAmount(match, self) > 0.8;

  // Only the two closest to him go; everyone else holds shape. Man-marked players are
  // counted among the two, as the original notes, because they are already occupied.
  const target = opponentImage.position.clone().addScaledVector(opponentImage.movement, 0.1);
  const closest = team.players
    .filter((player) => player.active)
    .sort((a, b) => a.position.distanceTo(target) - b.position.distanceTo(target))
    .slice(0, 2);
  if (!closest.includes(self)) return extraHaste ? { movement: fallback, extraHaste } : null;

  const destination = defendPosition(self, opponentImage, team.side);
  if (!needDefendingMovement(team.side, self.position, destination)) {
    return extraHaste ? { movement: fallback, extraHaste } : null;
  }

  const toDestination = destination.clone().sub(self.position);
  return {
    movement: {
      direction: normalizedOr(toDestination, fallback.direction),
      velocity: clamp(toDestination.length() * 2.6, 0, SPRINT_VELOCITY),
      lookAt: fallback.lookAt,
    },
    extraHaste,
  };
}

/**
 * How much faster this player can reach the ball than the opposing side can,
 * `playercontroller.cpp:616`. Above one means he gets there first.
 */
function playerPossessionAmount(match: Match, self: MatchPlayer): number {
  return (
    (match.opponentTeam(self.teamId).timeNeededToGetToBallMs + 200) /
    (self.timeNeeded.usualMs + 200)
  );
}

/** Everything the ball magnet needs that the match state does not already say. */
interface MagnetContext {
  /** The hunt and the ordered squeeze: go at the ball whoever is nominally on it. */
  forceMagnet?: boolean;
  extraHaste?: boolean;
  /**
   * For a player a person is driving, how recently control was handed to him: 1 at the
   * instant of the switch, fading to nothing over three tenths of a second
   * (`playercontroller.cpp:59-63`). Null when the computer is driving him, which is also how
   * the two human-only clauses below tell the difference.
   */
  humanSwitchBias?: number | null;
}

/** The shared movement command's ball magnet, applied only to the final fallback. */
function automaticMovement(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
  manual: DesiredMovement,
  context: MagnetContext = {},
): DesiredMovement {
  const forceMagnet = context.forceMagnet ?? false;
  const extraHaste = context.extraHaste ?? false;
  const humanSwitchBias = context.humanSwitchBias ?? null;
  if (match.ballRetainer === self) return manual;
  if (!allowedAtTheBall(match, self, forceMagnet)) return manual;
  if (self.hasBestPossession) {
    const target = image.ballPrediction(330).setZ(0);
    const toBall = target.clone().sub(self.position);
    const distance = toBall.length();
    const ballward = normalizedOr(toBall, self.directionVec);
    const facingBias = normalizedClamp(distance, 0.2, 0.4);
    // Where he goes when the ball is not pulling him anywhere, which once it is at his feet
    // is almost all of the time — `facingBias` is zero below twenty centimetres.
    //
    // THE SAME DIVERGENCE AS THE ONE BELOW, AND FOUND THE SAME WAY: BY BEING UNABLE TO PLAY.
    // The line used to read `self.directionVec` unconditionally, which for the computer's
    // players is right — a man with the ball carries on the way he was already going unless
    // the ball pulls him off it. For a person it means his own steering is thrown away
    // completely for as long as he has the ball. Measured in a running match, holding each
    // arrow in turn: a man WITHOUT the ball travelled at 0.90 of the way he was pointed, and
    // the same man WITH it at 0.67, sometimes flatly the other way. It reads as a player who
    // has stopped answering the controls, which is exactly what it is.
    //
    // So a person's direction takes the place of the heading here, and the pull onto the ball
    // still overrides it by the same amount at the same distances. Nothing changes for the
    // computer's players.
    const own =
      humanSwitchBias === null
        ? self.directionVec
        : normalizedOr(manual.direction, self.directionVec);
    const direction = normalizedOr(
      own.clone().multiplyScalar(1 - facingBias).addScaledVector(ballward, facingBias),
      own,
    );
    // How fast he needs to go merely to close the gap to the ball — and, since there is
    // hardly ever a gap once it is at his feet, nearly zero.
    //
    // DIVERGENCE, AND THE REASON FOR IT. The original throws his own intent away whenever
    // that closing speed falls below a jog (`AIfunctions.cpp:928-936`, whose "don't quantize
    // low velos" branch is empty), so a man standing over a ball that is not moving is told
    // to stand still. He then cannot dribble, and the ball cannot move, and nothing on the
    // pitch has any reason to change — over eight whole matches this froze play, with all
    // twenty-two men in an idle clip and the football clock running, for 27% of the time the
    // ball was live, and for half of two of those matches. A footballer with the ball at his
    // feet and five metres of grass in front of him does not stand there. So the closing
    // speed is a FLOOR under what he asked for rather than a replacement for it, which is
    // what the original itself does at every larger gap.
    const closingVelocity = clamp(distance * 2.6, 0, SPRINT_VELOCITY);
    return {
      direction,
      velocity: rangeVelocity(clamp(manual.velocity, closingVelocity, SPRINT_VELOCITY)),
      lookAt: target,
    };
  }
  const haste = extraHaste || forceMagnet || playerPossessionAmount(match, self) < 1.1;
  // The man the match says is on the ball, and anyone forced at it, runs at it outright.
  if (match.designatedPossessionPlayer === self || forceMagnet) {
    return getToBallMovement(self, image, manual, haste);
  }

  // Everybody else got here through one of the four extra clauses, and for them the run at
  // the ball is damped almost out of existence (`playercontroller.cpp:501-527`). Two things
  // bring it back: having touched the ball himself in the last two seconds, and being driven
  // by a person. With neither, the run is worth nothing and is not worth solving for either;
  // he keeps his own movement and only turns his head towards the ball.
  const recentTouch = self.lastTouchBias(2000, match.timeMs);
  if (recentTouch <= 0.01 && humanSwitchBias === null) {
    return blendedMovement(self, manual, watchTheBall(image, manual), 0);
  }

  const auto = getToBallMovement(self, image, manual, haste);
  const bias = magnetBias(match, self, manual, auto, recentTouch, humanSwitchBias);
  return bias >= 1 ? auto : blendedMovement(self, manual, auto, bias);
}

/**
 * Who is allowed to run at the ball at all, `playercontroller.cpp:467-472`.
 *
 * The man the match has settled on as being on the ball, always. Beyond him, his own side's
 * designated man may go too, in four more cases: he touched the ball himself within the last
 * two seconds, the other side is not holding it, he is at least as quick to it as they are,
 * or he already has it — the last three all requiring that he is not much slower to the ball
 * than the opposition. The original writes the "my side's man" test out four times over
 * rather than once around the group, and says in a comment that it is deliberate, for
 * clarity.
 *
 * The fourth clause cannot actually decide anything: having the ball at your feet and being
 * quicker to it than the opposition is precisely what the third clause already asks.
 */
function allowedAtTheBall(match: Match, self: MatchPlayer, forceMagnet: boolean): boolean {
  if (forceMagnet || match.designatedPossessionPlayer === self) return true;
  if (match.team(self.teamId).designatedPlayer !== self) return false;
  const possessionAmount = playerPossessionAmount(match, self);
  return (
    (self.lastTouchBias(2000, match.timeMs) > 0.01 && possessionAmount > 0.5) ||
    (!match.opponentTeam(self.teamId).hasPossession && possessionAmount > 0.5) ||
    // Air balls and the like, where several players are as likely as each other to get there.
    possessionAmount > 0.99 ||
    self.hasBestPossession
  );
}

/**
 * How much of the run at the ball survives for a man the match has not settled on,
 * `playercontroller.cpp:501-527`.
 *
 * A player who touched the ball himself moments ago gets some of it back, the more so the
 * more the run agrees with where he was going anyway, and only while he is within about a
 * second and three quarters of the ball. A person driving a player gets more than that while
 * the ball is loose: enough to chase a ball played to him while somebody else is nominally
 * on it — but not while he is running back towards his own goal, and not unless he is at
 * least as quick to the ball as the opposition.
 */
function magnetBias(
  match: Match,
  self: MatchPlayer,
  manual: DesiredMovement,
  auto: DesiredMovement,
  recentTouch: number,
  humanSwitchBias: number | null,
): number {
  const sameDirFactor =
    (1 - clamp(Math.abs(angle2D(auto.direction, manual.direction)) / Math.PI, 0, 1)) * 0.5 + 0.5;
  let bias = 0;
  if (recentTouch > 0.01 && self.timeNeeded.usualMs < 1700) {
    bias = Math.pow(recentTouch, 0.4) * Math.pow(sameDirFactor, 0.5);
  }
  if (!match.opponentTeam(self.teamId).hasPossession && humanSwitchBias !== null) {
    const towardsOwnGoal = normalizedClamp(manual.direction.x * match.team(self.teamId).side, -1, 1);
    const pull =
      Math.max(curve(sameDirFactor, 0.8), Math.sqrt(clamp(humanSwitchBias, 0, 1))) *
      (1 - towardsOwnGoal);
    const possessionAmount = playerPossessionAmount(match, self);
    bias = clamp(Math.sqrt((Math.max(possessionAmount, 0.5) - 0.5) * 2) * pull, bias, 1);
  }
  return bias;
}

/** The magnet with nothing left of it: his own movement, his eyes on the ball. */
function watchTheBall(image: MentalImage, manual: DesiredMovement): DesiredMovement {
  return {
    direction: manual.direction,
    velocity: manual.velocity,
    lookAt: image.ballPrediction(0).setZ(0),
  };
}

/**
 * The two movements mixed, `playercontroller.cpp:590-601`.
 *
 * Where he looks is not part of the mix: once he is allowed to go for the ball he watches it
 * whatever his feet are doing, and below a walking pace he faces it too.
 */
function blendedMovement(
  self: MatchPlayer,
  manual: DesiredMovement,
  auto: DesiredMovement,
  bias: number,
): DesiredMovement {
  const blended = manual.direction
    .clone()
    .multiplyScalar(manual.velocity * (1 - bias))
    .addScaledVector(auto.direction, auto.velocity * bias);
  const velocity = clamp(blended.length(), 0, SPRINT_VELOCITY);
  let direction = normalizedOr(blended, manual.direction);
  if (velocity < IDLE_DRIBBLE_SWITCH) {
    direction = normalizedOr(auto.lookAt.clone().sub(self.position).setZ(0), direction);
  }
  return { direction, velocity, lookAt: auto.lookAt };
}

/** Is the ball off the field at this moment of its flight, `AIfunctions.cpp:730-731,751-752`. */
function offThePitch(sample: Vector3): boolean {
  return Math.abs(sample.x) > PITCH.halfLength - 0.2 || Math.abs(sample.y) > PITCH.halfWidth - 0.2;
}

/**
 * The point on the ball's line of travel he stands closest to, `AIfunctions.cpp:655-658`.
 *
 * The line runs from where the ball is now to where it will be in a second, and is endless in
 * both directions — `line.cpp:60-81` never clamps the projection, so the foot of the
 * perpendicular may sit behind the ball or far beyond the second point.
 */
function closestPointOnBallLine(
  from: Vector3,
  to: Vector3,
  point: Vector3,
  out = new Vector3(),
): Vector3 {
  const alongX = to.x - from.x;
  const alongY = to.y - from.y;
  const lengthSq = alongX * alongX + alongY * alongY;
  if (lengthSq < 1e-12) return out.copy(from);
  const u = ((point.x - from.x) * alongX + (point.y - from.y) * alongY) / lengthSq;
  return out.set(from.x + u * alongX, from.y + u * alongY, 0);
}

const _sample = new Vector3();
const _toTarget = new Vector3();
const _toTargetUnit = new Vector3();
const _targetMovement = new Vector3();

function getToBallMovement(
  self: MatchPlayer,
  image: MentalImage,
  manual: DesiredMovement,
  haste: boolean,
): DesiredMovement {
  const ballNow = image.ballPrediction(0).setZ(0);
  const ballFuture = image.ballPrediction(1000).setZ(0);
  const roughBallDirection = normalizedOr(ballFuture.clone().sub(ballNow), self.directionVec);
  const desiredDirection = quantizeDirection16(manual.direction, roughBallDirection);
  const desiredMovement = desiredDirection.clone().multiplyScalar(manual.velocity);
  const ballLinePoint = closestPointOnBallLine(ballNow, ballFuture, self.position);

  // Which way the ball is really running at the moment he expects to arrive, taken over a
  // single tick there rather than over the whole second above (`AIfunctions.cpp:740-742`).
  const arrivalMs = self.timeNeeded.usualMs;
  const ballDirectionRough = normalizedOr(
    image.ballPrediction(arrivalMs + 10).setZ(0).sub(image.ballPrediction(arrivalMs).setZ(0)),
    desiredDirection,
  );

  // Start looking at the moment he could be there, but begin again at 80 ms if the ball has
  // already left the field by then: the original's own comment is that starting later "would
  // mean we'd aim outside the pitch to start with" (`AIfunctions.cpp:727-733`). Without the
  // restart, a chaser who cannot beat a clearance to the touchline runs at a point metres off
  // the field rather than at the spot where the ball crosses the line.
  let startMs = clamp(self.timeNeeded.usualMs, 40, BALL_PREDICTION_MS - 10);
  if (offThePitch(image.ballPrediction(startMs, _sample))) startMs = 80;

  let bestRating = -Infinity;
  // -1 is the original's "nothing chosen yet" (`AIfunctions.cpp:715`), which the two fallbacks
  // below both test for.
  let bestTime = -1;
  let bestNeeded = 0;
  const bestTarget = new Vector3();
  let timeStep = 1;

  for (let timeMs = startMs; timeMs < BALL_PREDICTION_MS; timeMs += 10 * timeStep) {
    const target = image.ballPrediction(timeMs, _sample);
    let forced = offThePitch(target);
    // Was a flat 1.0 m — a ball at a man's waist. Now genuinely over his head; see
    // `MatchPlayer.reachableBallHeight`.
    if (!forced && target.z >= self.reachableBallHeight) continue;
    target.setZ(0);
    const needed = timeNeededForDistanceMs(
      self.position,
      self.movement,
      target,
      self.maxVelocity,
      true,
      timeMs,
    ).usualMs;
    // Skip ahead in bigger jumps while the ball is hopelessly far away, down to 10 ms once he
    // can nearly make it (`AIfunctions.cpp:761-766`).
    timeStep = clamp(Math.round((needed - timeMs) * 0.05) - 5, 1, 10);
    if (needed <= timeMs) {
      // Two reasons to stop looking and commit to this moment (`AIfunctions.cpp:785-789`).
      // Either he has far more time than he needs, in which case every later interception is
      // a jog he does not want; or the point lies along the ball's own line of travel, where
      // trailing it down that line achieves nothing and he should just go and get it.
      const dot = normalizedOrZero(_toTarget.copy(target).sub(self.position), _toTargetUnit).dot(
        ballDirectionRough,
      );
      if (clamp(self.timeNeeded.usualMs / timeMs, 0, 1) < 0.35) forced = true;
      if (dot > 0) forced = true;

      const targetVelocity = clamp(target.distanceTo(self.position) * 2.6, 0, SPRINT_VELOCITY);
      const targetMovement = normalizedOr(_toTarget, self.directionVec, _targetMovement)
        .multiplyScalar(targetVelocity);
      const movementRating =
        1 - normalizedClamp(targetMovement.distanceTo(desiredMovement), 0, SPRINT_VELOCITY);
      const directionRating =
        1 -
        normalizedClamp(
          Math.acos(clamp(normalizedOr(targetMovement, self.directionVec).dot(desiredDirection), -1, 1)),
          0,
          Math.PI * 0.5,
        );
      const movementCombined = movementRating * 0.4 + directionRating * 0.6;
      const timeRating = 1 - normalizedClamp(timeMs, 0, 5000);
      // How far this interception point is from the ball's line, which is what makes cutting
      // across the ball score better than following it. It carries a tenth of the weight of
      // everything else, and nothing at all when he is in a hurry (`AIfunctions.cpp:645-710`).
      const perpendicularRating =
        1 - normalizedClamp(ballLinePoint.distanceTo(target), 0, 2 * SPRINT_VELOCITY);
      const rating = haste ? timeRating : movementCombined + perpendicularRating * 0.1;
      if (rating > bestRating || (forced && bestTime === -1)) {
        bestRating = rating;
        bestTime = timeMs;
        bestNeeded = needed;
        bestTarget.copy(target);
      }
    }
    if (forced) {
      if (bestTime === -1) {
        // He cannot reach even this point in time, so there is nothing to choose between:
        // aim at it and go flat out (`AIfunctions.cpp:834-837`).
        bestTime = timeMs;
        bestNeeded = timeMs;
        bestTarget.copy(target);
      }
      break;
    }
  }

  if (bestTime === -1) {
    // Nothing anywhere in the forecast was reachable: chase its last sample
    // (`AIfunctions.cpp:842-845`).
    bestTime = BALL_PREDICTION_MS - 10;
    bestNeeded = bestTime;
    image.ballPrediction(bestTime, bestTarget).setZ(0);
  }

  let velocity = clamp((bestNeeded / Math.max(bestTime, 1)) * self.maxVelocity, 0, SPRINT_VELOCITY);
  let direction = normalizedOr(bestTarget.clone().sub(self.position), self.directionVec);
  if (bestTime > 250) {
    const blended = direction
      .clone()
      .multiplyScalar(velocity * 0.97)
      .addScaledVector(self.movement, 0.03);
    velocity = clamp(blended.length(), 0, SPRINT_VELOCITY);
    direction = normalizedOr(blended, direction);
  }
  return { direction, velocity, lookAt: ballNow };
}

function quantizeDirection16(direction: Vector3, reference: Vector3): Vector3 {
  const referenceAngle = Math.atan2(reference.y, reference.x);
  const local = Math.atan2(direction.y, direction.x) - referenceAngle;
  const snapped = Math.round(local / (Math.PI / 8)) * (Math.PI / 8) + referenceAngle;
  return new Vector3(Math.cos(snapped), Math.sin(snapped), 0);
}

function actionCommand(type: string, movement: DesiredMovement): PlayerCommand {
  return {
    desiredFunctionType: type,
    useDesiredMovement: true,
    desiredDirection: movement.direction.clone(),
    desiredVelocityFloat: movement.velocity,
    useDesiredLookAt: true,
    desiredLookAt: movement.lookAt.clone(),
  };
}

function strikeCommand(
  type: 'shortpass' | 'longpass' | 'highpass' | 'shot',
  movement: DesiredMovement,
  direction: Vector3,
  power: number,
  confidenceBonus = 0,
): PlayerCommand {
  return {
    desiredFunctionType: type,
    useDesiredMovement: false,
    desiredDirection: movement.direction.clone(),
    desiredVelocityFloat: movement.velocity,
    useDesiredLookAt: false,
    desiredLookAt: movement.lookAt.clone(),
    touchInfo: {
      desiredDirection: normalizedOr(direction, movement.direction),
      desiredPower: clamp(power, 0, 1),
      confidenceBonus,
    },
  };
}

/**
 * Below this many metres from the off-ball target, the vector from him to it is too short to
 * trust as a direction; from this far out, trust it outright. Between the two, fade smoothly
 * rather than snap at a single cutoff.
 *
 * MEASURED, not guessed. A defender who has arrived at his defensive spot stands there while
 * the target driving `offBallMovement` keeps being recomputed underneath him — the
 * nearest-opponent set `supportPosition` repels from changing membership, a man-mark swapping
 * hands, the ball's own average position drifting a few centimetres — and every one of those
 * nudges the target by an amount that means nothing on a football pitch but is the WHOLE of a
 * normalised vector's direction once he is standing on it. Logging four full-backs' own
 * desired direction tick by tick over 90 seconds of a played match
 * (`.agent/scratch/fullback-spin-repro.ts`) found it swinging more than 90 degrees from the
 * tick before roughly once every 2.5 to 7 seconds — and the same pattern, at a similar rate,
 * on every other outfield role (`.agent/scratch/allroles-spin-repro.ts`), so this is the
 * off-ball shape logic every position shares, not something particular to a flank. At the
 * tick of the swing the target sat under 1.5 m away 21% to 62% of the time depending on the
 * player: a target that close cannot justify a real 90-degree change of mind about where to
 * stand, so the swing is the vector's own instability, not a football decision.
 *
 * 0.5 m sits inside the noise on any of the terms above. 2.5 m is the mental image's own
 * "close enough to be the same place" bound (`MAX_DISTANCE_DEVIATION`, `src/sim/mentalimage.ts`)
 * — by then a defender is far enough off his mark that a fresh direction is a real correction.
 */
export const OFF_BALL_TRUST_NEAR_M = 0.5;
export const OFF_BALL_TRUST_FAR_M = 2.5;

/** The off-ball movement direction, steadied against `OFF_BALL_TRUST_NEAR_M`/`_FAR_M`. */
export function offBallDirection(desired: Vector3, self: MatchPlayer, distance: number): Vector3 {
  const raw = normalizedOr(desired.clone().sub(self.position), self.directionVec);
  const trust = normalizedClamp(distance, OFF_BALL_TRUST_NEAR_M, OFF_BALL_TRUST_FAR_M);
  return normalizedOr(
    self.directionVec.clone().multiplyScalar(1 - trust).addScaledVector(raw, trust),
    raw,
  );
}

export function offBallMovement(match: Match, self: MatchPlayer, image: MentalImage): DesiredMovement {
  const team = match.team(self.teamId);
  const staticPosition = adaptedFormationPosition(match, self, false);
  const dynamicPosition = adaptedFormationPosition(match, self, true);
  const actionDistance = normalizedClamp(
    self.position.distanceTo(match.designatedPossessionPlayer.position),
    15,
    20,
  );
  const mindset = ROLE_OFFENSIVENESS[self.dynamicRole];
  const group = mindset < 0.4 ? 'def' : mindset < 0.7 ? 'mid' : 'off';
  const staticFactor = group === 'def' ? 1 : group === 'mid' ? 0.9 : 0.8;
  const staticBias = curve(staticFactor * actionDistance, 1);
  const desired = staticPosition.multiplyScalar(staticBias).addScaledVector(dynamicPosition, 1 - staticBias);

  const low = group === 'def' ? 0.2 : 0.1;
  const high = group === 'def' ? 0.9 : group === 'mid' ? 0.7 : 0.6;
  const attackBias = normalizedClamp(team.fadingPossessionAmount - 0.5, low, high);
  const makeRun =
    team.attackingRunPlayer === self &&
    team.attackingRunUntilMs > match.timeMs &&
    ((group === 'mid' && attackBias > 0.9) || (group === 'off' && attackBias > 0.7));
  const support = supportPosition(match, self, image, desired, makeRun);
  desired.multiplyScalar(1 - attackBias).addScaledVector(support, attackBias);

  const defensiveK = group === 'def' ? 1.9 : group === 'mid' ? 1.5 : 1.3;
  const defensiveBias = Math.pow(
    clamp(defensiveK - mindset - team.fadingPossessionAmount, 0, 1),
    0.7,
  );
  addDefensiveComponent(match, self, image, desired, defensiveBias);
  if (group !== 'off') applyOffsideTrap(desired, team);

  const distance = desired.distanceTo(self.position);
  return {
    // Fading towards his current heading below OFF_BALL_TRUST_NEAR_M, rather than normalising
    // the raw target vector outright, is what stops him spinning on the spot. See the note
    // above offBallDirection.
    direction: offBallDirection(desired, self, distance),
    velocity: lazyVelocity(match, self, distance * 2.6),
    lookAt: match.ball.predict(200, new Vector3()).setZ(0),
  };
}

/**
 * Where the attacking-run pull's power is centred on `mental_offensivepositioning`, so a player
 * sitting at the attribute's population middle reproduces today's flat power of 2 exactly and
 * only a player away from that middle diverges from it.
 *
 * ≈ centre of the attribute's in-play range (0.18–1.00, `docs/design/data.md`). Chosen, not
 * measured.
 */
const ATTACKING_POSITIONING_REFERENCE = 0.59;
/** How far the attacking-run pull's power swings either side of 2 across that range. Chosen, not measured. */
const ATTACKING_POSITIONING_RUN_SLOPE = 1.5;

export function supportPosition(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
  base: Vector3,
  makeRun: boolean,
): Vector3 {
  const team = match.team(self.teamId);
  const owner = match.designatedPossessionPlayer;
  const mindset = ROLE_OFFENSIVENESS[self.dynamicRole];
  const shiftedBase = base.clone();
  if (team.forwardSupportPlayer === self) {
    shiftedBase.x += -team.side * (0.3 + 0.7 * mindset) * 12;
  } else {
    const laneY = -signSide(owner.position.y) * 8;
    const amount = 22 * curve(1 - normalizedClamp(Math.abs(laneY - self.position.y), 0, 30), 1);
    shiftedBase.x += -team.side * Math.pow(mindset, 1.5) * amount;
  }

  const field: ForceSpot[] = [{ origin: shiftedBase, power: 0.7, scale: Infinity, repel: false, exponent: 1 }];
  if (makeRun) {
    field.push({
      origin: new Vector3(-team.side * PITCH.halfLength, self.position.y * 0.5, 0),
      power:
        2 +
        ATTACKING_POSITIONING_RUN_SLOPE *
          (self.physics.stat('mental_offensivepositioning') - ATTACKING_POSITIONING_REFERENCE),
      scale: Infinity,
      repel: false,
      exponent: 1,
    });
  }
  const roleMultiplier =
    self.dynamicRole === 'CB' || self.dynamicRole === 'LB' || self.dynamicRole === 'RB'
      ? 2.2
      : self.dynamicRole === 'DM'
        ? 2
        : self.dynamicRole === 'CM' || self.dynamicRole === 'LM' || self.dynamicRole === 'RM'
          ? 1.6
          : self.dynamicRole === 'AM'
            ? 1.2
            : 1;
  nearestImages(match.opponentTeam(self.teamId).players, image, self.position, 3).forEach((opp) => {
    const awayFromOwner = normalizedOr(opp.position.clone().sub(owner.position), FORWARD);
    field.push({
      origin: opp.position.clone().addScaledVector(opp.movement, 0.1).addScaledVector(awayFromOwner, 2),
      power: 0.3 * roleMultiplier * (makeRun ? 0.5 : 1),
      scale: makeRun ? 2 : 5,
      repel: true,
      exponent: 0.7,
    });
  });
  if (team.fadingPossessionAmount >= 1.02) {
    nearestImages(team.players.filter((p) => p !== self), image, self.position, 6).forEach((mate) => {
      field.push({
        origin: mate.position.clone().addScaledVector(mate.movement, 0.1),
        power: 0.4,
        scale: 10.5,
        repel: true,
        exponent: 1,
      });
    });
  }
  if (team.fadingPossessionAmount >= 1.06 && owner !== self) {
    for (const ms of [200, 350, 500, 650]) {
      field.push({
        origin: image.ballPrediction(ms),
        power: 1,
        scale: 2,
        repel: true,
        exponent: 0.5,
      });
    }
  }
  const ownerImage = image.player(owner.id);
  if (ownerImage && owner.teamId === self.teamId) {
    field.push({ origin: ownerImage.position, power: 0.45, scale: 21, repel: false, exponent: 1 });
    field.push({ origin: ownerImage.position, power: 0.45, scale: 12, repel: true, exponent: 1 });
  }
  const movement = forceField(field, self.position, 7);
  const result = self.position.clone().add(movement);
  const line = offsideLine(match, match.opponentTeam(self.teamId).id, image, 240);
  if (result.x * -team.side > line * -team.side - 0.08) result.x = line + team.side * 0.08;
  return result;
}

function addDefensiveComponent(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
  desired: Vector3,
  bias: number,
): void {
  if (bias <= 0) return;
  const team = match.team(self.teamId);
  const marked = match.opponentTeam(self.teamId).players.find((p) => p.id === self.manMarkingId);
  const opponent = marked ?? match.opponentTeam(self.teamId).designatedPlayer;
  const oi = image.player(opponent.id);
  if (!oi) return;
  const shootThreshold = opponent === match.designatedPossessionPlayer ? 24 : 8;
  const oppPos = oi.position.clone().addScaledVector(oi.movement, 0.5);
  const goal = new Vector3(PITCH.halfLength * team.side, 0, 0);
  const oppToGoal = goal.clone().sub(oppPos);
  const thresholdDistance = clamp(oppToGoal.length() - shootThreshold, 0.4, PITCH.halfLength);
  let shootingPoint = oppPos
    .clone()
    .addScaledVector(normalizedOr(oppToGoal, new Vector3(team.side, 0, 0)), thresholdDistance);
  if (shootingPoint.x * team.side > team.offsideTrapX * team.side) {
    const denominator = goal.x - oppPos.x;
    const u = denominator === 0 ? 0 : (team.offsideTrapX - oppPos.x) / denominator;
    shootingPoint = oppPos.clone().lerp(goal, u);
  }
  const toThreshold = shootingPoint.clone().sub(desired);
  const slack = toThreshold.length() - (thresholdDistance - 4);
  const defend = desired.clone();
  if (slack > 0) defend.addScaledVector(normalizedOr(toThreshold, FORWARD), clamp(slack, 0, toThreshold.length()));
  const actualSlack =
    shootingPoint.distanceTo(self.position.clone().addScaledVector(self.movement, 0.14)) -
    thresholdDistance;
  if (actualSlack > 0) defend.addScaledVector(normalizedOr(goal.clone().sub(defend), FORWARD), actualSlack * 0.7);
  desired.multiplyScalar(1 - bias).addScaledVector(defend, bias);
}

/**
 * The second half of a wall pass — "pared": give it, sprint into space, get it straight back.
 *
 * `applyAttackingRun` (`src/input/player.ts`) is the first half and already existed: a human
 * holding SWITCH while on the ball nominates his nearest team-mate, and `supportPosition`
 * above already pulls that man forward for it. The missing piece was the return — so when that
 * same human then plays a short pass to the very team-mate he nominated, `armWallPassReturn`
 * (also `src/input/player.ts`) notes it on the team, and this is what the nominee is offered
 * once the ball is his: an instant one-touch pass back, led into the passer's run right now
 * rather than aimed at the spot he passed from (`passingOdds`'s own lead already does that).
 *
 * Built exactly like every other pass — the same `strikeCommand`, the same reach search and
 * action smuggle in `anim/touch.ts` — so there is nothing exotic about what he does, only about
 * when he is asked to do it: this is offered ahead of the trap in `playerCommands`, so a
 * reachable return wins outright, and an unreachable one falls through to `null` and he
 * receives the ball exactly as if nothing were armed.
 *
 * Never armed by the computer's own play. `updateAttackingRun` in `teamai.ts` nominates a run
 * for the AI too, using these same two team fields, but nothing in the computer's own command
 * building ever calls `armWallPassReturn` — only a human's own short pass does. An AI-versus-AI
 * match cannot reach this function at all.
 */
function wallPassReturnCommand(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
  movement: DesiredMovement,
): PlayerCommand | null {
  const team = match.team(self.teamId);
  const passer = team.wallPassPasser;
  if (
    team.wallPassReceiver !== self ||
    !passer?.active ||
    match.timeMs > team.wallPassUntilMs ||
    match.ballRetainer ||
    self.hasPossession ||
    image.ballPrediction(200).distanceTo(self.position) > 10
  ) {
    return null;
  }
  const evaluated = passingOdds(match, self, passer, image, 'shortpass');
  const origin = self.position.clone().addScaledVector(self.movement, 0.12);
  const aim = autoPass(evaluated.target.clone().sub(origin).setZ(0), false);
  return strikeCommand('shortpass', movement, aim.direction, aim.power);
}

function onBallCommands(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
): { commands: PlayerCommand[]; movement: DesiredMovement } {
  const movement = dribbleMovement(match, self, image);
  const commands: PlayerCommand[] = [];
  const team = match.team(self.teamId);
  const mindset = ROLE_OFFENSIVENESS[self.dynamicRole];
  const ownSituation = tacticalSituation(match, self, image);
  const movementDiff = normalizedClamp(match.ball.momentum.clone().sub(self.movement).length(), 0, 10);
  const oneTouchHard = movementDiff - self.physics.stat('technical_shortpass') * movementDiff * 0.8;
  const possessionDuration = Math.max(0, match.timeMs - team.lastTouchTimeMs);
  const longPossession = Math.pow(normalizedClamp(possessionDuration, 0, 5000), 2);
  const forwardWeight = 2 + mindset * 6;
  const total1 = 0.4 + 0.3 + forwardWeight;
  const ownRating =
    (ownSituation.forwardSpaceRating * 0.4 + ownSituation.spaceRating * 0.3 + ownSituation.forwardRating * forwardWeight) /
    total1;
  const threshold = 0.06 * (1 - mindset);
  const tacticalWeight = 1 + mindset * mindset * 10;
  const passMinimum = 0.2 * (1 - mindset) - longPossession * 0.1;
  const total2 = tacticalWeight + 1;
  const passThreshold = 0.1 - longPossession * 0.05;
  let best: {
    player: MatchPlayer;
    type: PassType;
    target: Vector3;
    rating: number;
    passRating: number;
  } | null = null;

  for (const mate of team.players) {
    if (mate === self) continue;
    const situation = tacticalSituation(match, mate, image);
    let mateRating =
      (situation.forwardSpaceRating * 0.4 + situation.spaceRating * 0.3 + situation.forwardRating * forwardWeight) /
      total1;
    if (mate.role === 'GK') mateRating *= 0.7;
    if (mateRating <= ownRating + threshold) continue;
    for (const type of ['shortpass', 'longpass', 'highpass'] as const) {
      const evaluated = passingOdds(match, self, mate, image, type, 1);
      const total = ((mateRating - ownRating) * tacticalWeight + evaluated.odds - oneTouchHard) / total2;
      if (total > passThreshold && evaluated.odds > passMinimum && (!best || total > best.rating)) {
        best = {
          player: mate,
          type,
          target: evaluated.target,
          rating: total,
          passRating: evaluated.odds,
        };
      }
    }
  }

  // Panic passes precede the calculated pass and shot in the original command queue.
  if (mindset < 0.25) {
    const panic = 1 - mindset * 2;
    const goalCloseness =
      1 - normalizedClamp(self.position.distanceTo(new Vector3(PITCH.halfLength * team.side, 0, 0)), 2, 16);
    if (
      (!best || best.passRating < panic * goalCloseness) &&
      team.possessionAmount < 0.9 + panic * goalCloseness * 0.8
    ) {
      const ySide = signSide(self.directionVec.y);
      const direction = normalizedOr(
        new Vector3(self.directionVec.x * 0.8 - team.side * 0.7, self.directionVec.y + ySide * 0.5, 0.3),
        new Vector3(-team.side, 0, 0.3),
      );
      commands.push(strikeCommand('highpass', movement, direction, 0.7));
      commands.push(strikeCommand('shot', movement, direction, 0.6));
      commands.push(strikeCommand('longpass', movement, direction, 0.8));
    }
  }
  if (best) {
    const origin = self.position.clone().addScaledVector(self.movement, 0.12);
    // Through the same auto-pass the set pieces use, so a high ball from open play actually
    // goes up. This used to flatten the direction outright and read the power off the
    // distance over thirty metres, which made every "high pass" a ground pass.
    const aim = autoPass(best.target.clone().sub(origin).setZ(0), best.type === 'highpass');
    commands.push(strikeCommand(best.type, movement, aim.direction, aim.power));
  }

  // Directly above the shot, because the two answer the same question and the chip answers it
  // better in exactly one situation: the keeper is off his line and cannot get back. Everywhere
  // else it returns nothing and the shot below runs as it always did.
  const chip = chipCommand(match, self, movement);
  if (chip) commands.push(chip);

  const shot = shotCommand(match, self, image, movement);
  if (shot) commands.push(shot);
  return { commands, movement };
}

type PassType = 'shortpass' | 'longpass' | 'highpass';

export function passingOdds(
  match: Match,
  self: MatchPlayer,
  mate: MatchPlayer,
  image: MentalImage,
  type: PassType,
  ballVelocityMultiplier = 1,
): { odds: number; target: Vector3 } {
  const mateImage = image.player(mate.id);
  const initial = mateImage?.position ?? mate.position;
  const distance = initial.distanceTo(self.position);
  if (type === 'highpass' && distance < 10) return { odds: 0, target: initial.clone() };
  const estimatedTime = 0.7 + distance * 0.03;
  const target = initial
    .clone()
    .addScaledVector(mateImage?.movement ?? mate.movement, clamp(estimatedTime, 0, 0.5));
  if (type === 'longpass') target.x += -match.team(self.teamId).side * distance * 0.2;
  const origin = self.position.clone().addScaledVector(self.movement, 0.12);
  const line = target.clone().sub(origin);
  const lineLengthSq = line.lengthSq();
  let danger = 0;
  for (const opponent of match.opponentTeam(self.teamId).players) {
    const oi = image.player(opponent.id);
    const position = (oi?.position ?? opponent.position).clone().addScaledVector(oi?.movement ?? opponent.movement, 0.2);
    const u = lineLengthSq === 0 ? 0 : position.clone().sub(origin).dot(line) / lineLengthSq;
    if (u < 0 || u > 1.2 || (type === 'highpass' && u >= 0.2 && u <= 0.65)) continue;
    const intersection = origin.clone().addScaledVector(line, clamp(u, 0, 1));
    const opponentTime = (position.distanceTo(intersection) + 1) / SPRINT_VELOCITY;
    const penalty = type === 'highpass' && u > 0.5 ? 2.5 : 0;
    // The extra `u` is in the original even though intersection already contains it.
    const ballTime = (0.7 + intersection.distanceTo(origin) * u * 0.03 + penalty) / ballVelocityMultiplier;
    danger += clamp(ballTime - opponentTime + 0.5, 0, 1);
  }
  if (type === 'highpass') danger += 0.4;
  return { odds: 1 - normalizedClamp(danger, 0, 1), target };
}

function shotCommand(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
  movement: DesiredMovement,
): PlayerCommand | null {
  const side = match.team(self.teamId).side;
  const goal = new Vector3(-side * PITCH.halfLength, 0, 0);
  const goalDistance = normalizedClamp(self.position.distanceTo(goal), 0, 32);
  const ideal = curve(
    1 - normalizedClamp(self.position.distanceTo(new Vector3((PITCH.halfLength - 7) * -side, 0, 0)), 0, 16),
    1,
  );
  if (ideal <= 0.1) return null;
  let bestY = 0;
  let bestOdds = -1;
  for (const y of [-3.6, 0, 3.6]) {
    const target = new Vector3((PITCH.halfLength + 1) * -side, y, 0);
    const odds = passingOddsToPoint(match, self, image, target, 'shortpass', 3);
    if (odds > bestOdds) {
      bestOdds = odds;
      bestY = y;
    }
  }
  if (Math.sqrt(bestOdds) + deterministicRandom(match, self.id, 1) * 0.5 <= 0.5) return null;
  bestY +=
    (deterministicRandom(match, self.id, 2) * 2 - 1) *
    (1 - self.physics.stat('technical_shot'));
  let direction = new Vector3((PITCH.halfLength + 1) * -side, bestY, 0)
    .sub(self.position.clone().addScaledVector(self.movement, 0.2))
    .normalize();
  direction = normalizedOr(
    direction.multiplyScalar(0.7).addScaledVector(self.directionVec, (-self.floatVelocity / SPRINT_VELOCITY) * 0.3),
    new Vector3(-side, 0, 0),
  );
  const power =
    (0.7 + deterministicRandom(match, self.id, 3) * 0.3) * (0.6 + goalDistance * 0.4);
  return strikeCommand('shot', movement, direction, power);
}

function passingOddsToPoint(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
  target: Vector3,
  type: PassType,
  multiplier: number,
): number {
  const origin = self.position.clone().addScaledVector(self.movement, 0.12);
  const line = target.clone().sub(origin);
  let danger = 0;
  for (const opponent of match.opponentTeam(self.teamId).players) {
    const oi = image.player(opponent.id);
    const pos = (oi?.position ?? opponent.position).clone().addScaledVector(oi?.movement ?? opponent.movement, 0.2);
    const u = line.lengthSq() === 0 ? 0 : pos.clone().sub(origin).dot(line) / line.lengthSq();
    if (u < 0 || u > 1.2 || (type === 'highpass' && u >= 0.2 && u <= 0.65)) continue;
    const intersect = origin.clone().addScaledVector(line, clamp(u, 0, 1));
    const oppTime = (pos.distanceTo(intersect) + 1) / SPRINT_VELOCITY;
    const penalty = type === 'highpass' && u > 0.5 ? 2.5 : 0;
    danger += clamp((0.7 + intersect.distanceTo(origin) * u * 0.03 + penalty) / multiplier - oppTime + 0.5, 0, 1);
  }
  if (type === 'highpass') danger += 0.4;
  return 1 - normalizedClamp(danger, 0, 1);
}

/**
 * How far off the line from the striker to the goal a keeper may stand and still be worth
 * lobbing. Beyond this he is not in the way at all and an ordinary shot past him is the better
 * ball — which is exactly what the queue does next, since the chip sits directly above the shot.
 */
const CHIP_KEEPER_LANE_M = 2.5;

/**
 * How far above a keeper's raised hands the ball has to pass before the act is offered.
 *
 * Not zero, because "the ball is a centimetre above his fingertips where he is standing right
 * now" is not a chip, it is a coin toss: he has the whole flight to take a step and jump. See
 * the census in `PENDING.md` for what each value of this offers.
 */
const CHIP_CLEARANCE_M = 0.6;

/**
 * How far off his line the keeper has to be before the act is even considered.
 *
 * Measured rather than chosen: over six matches, with a man on the ball between 6 and 40 m from
 * goal, the opposing keeper was inside 1.7 m of his line on nine ticks in ten and inside 2.2 m
 * on ninety-nine in a hundred. So four metres is beyond the whole ordinary distribution and only
 * bites when he has genuinely come out for something — which is the situation the act belongs
 * to. The same reasoning the referee's field of view uses.
 */
const CHIP_KEEPER_OUT_M = 4;

/**
 * How fast a keeper gets back towards his own goal, in metres a second, for working out whether
 * he is beaten before the ball is even struck.
 *
 * THIS TEST IS THE ACT. Without it the chip is decoration: a keeper who gets home stands under
 * the ball and catches it at 1.65 m, well inside his reach, so "the ball would clear his hands
 * where he is standing right now" accepts a great many lobs that are simply caught. What beats a
 * keeper is being unable to recover, and the ball hangs for the best part of two seconds.
 *
 * The speed is a man who has to turn, find the ball over his shoulder and go backwards under it,
 * not a man sprinting on a clear run — which is why it is well under half of one. Chosen rather
 * than measured; what is measured is what each value of it offers, in `PENDING.md`.
 */
const CHIP_KEEPER_RECOVERY = 2.5;

/** The furthest out a chip is football rather than a hopeful lob. */
const CHIP_RANGE_M = 25;

/**
 * The chip: a ball lifted over a goalkeeper and dropped under his own bar.
 *
 * IT CANNOT GO THROUGH THE SHOT CONTACT. `shotVector` throws away the height of whatever
 * direction it is handed and re-sets it to 0.05, about three degrees, so no shot in this game
 * can ever be lofted; a pass keeps the lift it is given. A chip is therefore a HIGH PASS at the
 * goal, launched at 35 degrees with its power solved for the range rather than charged on a key
 * or read off a distance. `src/sim/chip.ts` does the solving and explains why the textbook range
 * formula is the wrong physics for it.
 *
 * THE CLIP LIBRARY DECIDES WHO MAY TRY ONE. Forward high-pass clips exist at a standstill, at a
 * dribble and at a walk, and there is none at a sprint — `highpass/sprint/090` and its mirror
 * send the ball sideways. So a man at full pelt asking for this selects nothing whatever and
 * falls through to the next command in his queue. That is not tested here, exactly as it is not
 * tested for the overhead kick: the animation filter is what knows.
 */
function chipStrike(
  self: MatchPlayer,
  goal: Vector3,
  flat: Vector3,
  movement: DesiredMovement,
): { command: PlayerCommand; flight: ChipFlight; range: number } | null {
  const origin = self.position.clone().addScaledVector(self.movement, 0.12);
  const direction = chipDirection(flat, self.directionVec);
  // How far the ball runs ALONG THE GROUND before it crosses the goal line, which is neither how
  // far the goal is — a man out wide aiming across the face of it needs a longer ball than his
  // distance to the near post — nor how far it travels, since the direction is tilted up and the
  // solver deals in ground distance. Reading it off the tilted direction instead makes every
  // chip a fifth too long.
  const ground = normalizedOr(flat.clone().setZ(0), self.directionVec).setZ(0).normalize();
  const alongX = goal.x - origin.x;
  if (Math.abs(ground.x) < 1e-3 || alongX / ground.x <= 0) return null;
  const toLine = alongX / ground.x;
  if (toLine > CHIP_RANGE_M) return null;

  const flight = solveChip(toLine + CHIP_OVERSHOOT_M);
  return {
    command: strikeCommand('highpass', movement, direction, passPowerForSpeed(flight.speed)),
    flight,
    range: toLine,
  };
}

/**
 * The computer's chip, and it is the whole of the act's gate.
 *
 * A KEEPER IS BEATEN BY A LOB WHEN HE CANNOT GET HOME, not merely when the ball would clear his
 * hands where he happens to be standing. The second test on its own is far too generous: the
 * ball hangs for a second and a half and a keeper a stride off his line simply steps back under
 * it. So both are asked — the ball passes clear of his reach at his own position, AND he cannot
 * cover the ground back to his line in the time the ball is in the air.
 *
 * It sits directly above the ordinary shot in the queue, which is the right place for it in both
 * directions: a chip is preferred to a shot exactly when the keeper is stranded, and whenever
 * this returns nothing the shot is tried immediately after, unchanged.
 */
function chipCommand(
  match: Match,
  self: MatchPlayer,
  movement: DesiredMovement,
): PlayerCommand | null {
  if (match.ballRetainer || self.role === 'GK' || !self.hasPossession) return null;
  const side = match.team(self.teamId).side;
  const goal = new Vector3(-side * PITCH.halfLength, 0, 0);
  const keeper = match.opponentTeam(self.teamId).players.find(
    (player) => player.role === 'GK' && player.active,
  );
  if (!keeper) return null;

  const chip = chipStrike(self, goal, goal.clone().sub(self.position), movement);
  if (!chip) return null;

  // Where the keeper falls along the ball's own line, and how far off it he is standing.
  const line = normalizedOr(goal.clone().sub(self.position).setZ(0), self.directionVec);
  const toKeeper = keeper.position.clone().sub(self.position).setZ(0);
  const along = toKeeper.dot(line);
  if (along <= 0 || along >= chip.range) return null;
  if (toKeeper.addScaledVector(line, -along).length() > CHIP_KEEPER_LANE_M) return null;

  const outFromLine = Math.abs(goal.x - keeper.position.x);
  if (outFromLine < CHIP_KEEPER_OUT_M) return null;

  // His hand meets the BOTTOM of the ball, so the centre of it clears him by a radius more.
  const reach = keeper.reachableBallHeight * STANDING_REACH_RATIO + BALL.radius;
  if (chip.flight.heightAt(along) < reach + CHIP_CLEARANCE_M) return null;

  if (outFromLine / CHIP_KEEPER_RECOVERY <= chip.flight.timeAt(chip.range)) return null;

  return chip.command;
}

/**
 * A person's chip. His finger is the gate, so none of the computer's tests are asked: if he
 * wants to lob a keeper standing on his own line and watch it drop into his arms, that is his
 * to get wrong.
 *
 * Aimed like his shot is — at the goal, bent by however hard he is pushing away from it — so the
 * same press beats a keeper down either side of him. The one thing he does not do is charge it:
 * the power is solved from the range, which is what makes it a chip rather than a high ball.
 */
function humanChipCommand(
  match: Match,
  self: MatchPlayer,
  intent: HumanPlayerIntent,
  movement: DesiredMovement,
): PlayerCommand | null {
  if (match.ballRetainer || self.role === 'GK' || !self.hasBestPossession) return null;
  const side = match.team(self.teamId).side;
  const goal = new Vector3(-side * PITCH.halfLength, 0, 0);
  const inputDirection = normalizedOr(intent.direction, self.directionVec);
  const goalDirection = normalizedOr(goal.clone().sub(self.position), inputDirection);
  const assist = intent.keyboard ? 1 : 0.2;
  const flat = normalizedOr(
    inputDirection.clone().multiplyScalar(1 - assist).addScaledVector(goalDirection, assist),
    goalDirection,
  );
  return chipStrike(self, goal, flat, movement)?.command ?? null;
}

function dribbleMovement(match: Match, self: MatchPlayer, image: MentalImage): DesiredMovement {
  const side = match.team(self.teamId).side;
  const future = self.position.clone().addScaledVector(self.movement, 0.25);
  const field: ForceSpot[] = [];
  nearestImages(match.opponentTeam(self.teamId).players, image, self.position, 5).forEach((opp) => {
    field.push({
      origin: opp.position.clone().addScaledVector(opp.movement, 0.25),
      power: 2,
      scale: 10,
      repel: true,
      exponent: 1,
    });
  });
  field.push({
    origin: new Vector3(self.position.x, (PITCH.halfWidth + 5) * signSide(self.position.y), 0),
    power: 4,
    // How far infield the touchline starts to push a dribbler away, shortened from 20 m.
    //
    // DIVERGENCE, AND THE REASON FOR IT. The repelling point sits 5 m beyond the line, so a
    // reach of 20 m meant a man began being shoved back towards the middle of the pitch as
    // soon as he came within 15 m of the touchline — with more than five times the pull of
    // the goal he is attacking. Nobody could ever run down a wing. The original's own author
    // wrote "stop going to the sides! wtf, todo, why does it prefer the sideline so much"
    // over the line above this one (`AIfunctions.cpp:405`), which says what this was for: in
    // his game players hugged the line because there was nobody out there to stop them. Here
    // the side is already narrow, so the same wall only compounds it. Eight metres keeps its
    // real job — a dribbler does not run off the pitch — and gives the last three metres
    // before the line back to the football.
    scale: 8,
    repel: true,
    exponent: 0.7,
  });
  field.push({
    origin: new Vector3((PITCH.halfLength + 5) * signSide(self.position.x), self.position.y, 0),
    power: 4,
    scale: 20,
    repel: true,
    exponent: 0.7,
  });
  const nearBackline = normalizedClamp(Math.abs(self.position.x) / PITCH.halfLength, 0, 1);
  const centerModifier = (1 - Math.pow(nearBackline, 2)) * 0.5;
  field.push({
    origin: new Vector3(-side * PITCH.halfLength, self.position.y * 0.5 * centerModifier, 0),
    power: 0.7 + ROLE_OFFENSIVENESS[self.dynamicRole] * 0.05,
    scale: Infinity,
    repel: false,
    exponent: 1,
  });
  const vector = forceField(field, future, 1);
  // A man with the ball at his feet moves with it, at worst at a jog.
  //
  // DIVERGENCE, AND THE REASON FOR IT. The field above is a tug of war between the goal he
  // is attacking and the men in front of him, and its two sides can cancel exactly. The
  // original then asks for whatever tiny speed is left (`AIfunctions.cpp:471-473`), and the
  // animation set has nothing between a standstill and a 3.5 m/s jog, so anything under
  // 1.8 m/s becomes a PERFECT standstill. Once he stops, the ball stops; once the ball
  // stops, the men marking him are already where they want to be and stop too; and nothing
  // left on the pitch has any reason to change. Measured over whole matches, play froze
  // solid — twenty-two men in an idle clip, the ball motionless, the football clock still
  // running — for a quarter to a half of every match, and in one match it froze away from
  // the halfway line and the referee never blew for half time. No footballer stands over a
  // still ball for ten seconds with five metres of grass in front of him, so the floor here
  // is a jog rather than nothing, in whatever direction he is already facing.
  const velocity = rangeVelocity(
    clamp(vector.length() * 2.6, DRIBBLE_VELOCITY, SPRINT_VELOCITY),
  );
  const direction = normalizedOr(vector, self.directionVec);
  return {
    direction,
    velocity,
    lookAt: self.position.clone().addScaledVector(direction, 10),
  };
}

function tacticalSituation(match: Match, player: MatchPlayer, image: MentalImage) {
  const side = match.team(player.teamId).side;
  const pi = image.player(player.id);
  const position = pi?.position ?? player.position;
  const movement = pi?.movement ?? player.movement;
  const forward = position.clone().add(new Vector3(-side * SPRINT_VELOCITY * 0.5, 0, 0));
  const situation = {
    forwardSpaceRating: freeSpace(match, player.teamId, image, forward, 5, 0.5, true),
    spaceRating: freeSpace(
      match,
      player.teamId,
      image,
      position.clone().addScaledVector(movement, 0.1),
      5,
      0.1,
      true,
    ),
    forwardRating: Math.pow(
      1 - normalizedClamp(position.distanceTo(new Vector3(-side * PITCH.halfLength, 0, 0)), 0, 110),
      1.5,
    ),
  };
  player.tacticalSituation = situation;
  return situation;
}

export function freeSpace(
  match: Match,
  teamId: number,
  image: MentalImage,
  focus: Vector3,
  safeDistance: number,
  futureSeconds: number,
  ignoreKeeper: boolean,
): number {
  let crowding = 0;
  for (const opponent of match.opponentTeam(teamId).players) {
    const oi = image.player(opponent.id);
    if (!oi || (ignoreKeeper && oi.dynamicRole === 'GK')) continue;
    const position = oi.position.clone().addScaledVector(oi.movement, 0.2);
    const distance = focus.clone().sub(position);
    const toward = normalizedToMax(distance, SPRINT_VELOCITY * clamp(futureSeconds - 0.2, 0, 1000));
    position.add(toward);
    crowding += 1 - clamp(position.distanceTo(focus), 0, safeDistance) / safeDistance;
  }
  return 1 - normalizedClamp(crowding, 0, 2.5);
}

function ballControlCommand(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
  movement: DesiredMovement,
): PlayerCommand | null {
  if (match.ballRetainer || image.ballPrediction(200).distanceTo(self.position) > 10) return null;
  if (!self.hasPossession && !match.allowLastDitch(self)) {
    if (Math.abs(match.ball.momentum.z) > 15) return null;
    if (new Vector3(match.ball.momentum.x, match.ball.momentum.y, 0).sub(self.movement).length() > 15) return null;
  }
  const teamDesignated = match.team(self.teamId).designatedPlayer === self;
  if (match.designatedPossessionPlayer !== self && !(teamDesignated && duelLikeliness(match, self, image) >= 0.25)) return null;
  return actionCommand('ballcontrol', movement);
}

/**
 * When the one bicycle-kick clip strikes the ball, and how high it wants it to be.
 *
 * `shot/idle/180_bicycle` is 64 frames long and its contact is frame 33, so the decision to
 * play it is taken a third of a second before the boot meets the ball. It wants the ball 1.80 m
 * up — the figure every clip in the library is drawn at a 1.92 m man, and the reach search
 * scales it by this player's own height, so the ratio rather than the metres is what belongs
 * here. For an average man that is a fraction under 1.7 m: over his own head.
 *
 * THE THIRD OF A SECOND IS THE WHOLE PROBLEM, and it is why this decision could not simply be
 * left to the header one above it. That one offers a shot whenever the ball will be above the
 * man's chest AT THE MOMENT HE CAN FIRST MEET IT, which for a man standing under a dropping
 * ball is the instant it falls past his own height. Three tenths of a second later — which is
 * when the bicycle's boot arrives — the ball has fallen another half-metre and is nowhere near
 * where the clip wants it. Measured over four matches, the reach search was handed the bicycle
 * 53 times and rejected it 53 times, every one of them on height.
 */
const BICYCLE_CONTACT_MS = 330;
const BICYCLE_BALL_HEIGHT_RATIO = 1.8 / 1.92;

/**
 * How far the ball may be off the clip's own contact point and still be worth trying, in metres.
 *
 * Neither is the real gate — the reach search runs afterwards and is stricter — they are only
 * there to stop a shot command being issued for a ball obviously somewhere else. The height
 * slack is a little under what the search itself allows a ball at this height (about 0.39 m
 * once its two high-ball relaxations are applied), and the distance is inside the region the
 * search grants a standing man (about 0.9 m).
 */
const BICYCLE_HEIGHT_SLACK_M = 0.35;
const BICYCLE_REACH_M = 0.8;

/**
 * How close an opponent has to be before a man will hook a clearance over his own head.
 *
 * MEASURED AGAINST THE DISTRIBUTION RATHER THAN CHOSEN. Ungated, the clearance branch produced
 * 8.25 overhead kicks a match, which is one every three minutes of football and far too many
 * for something that ends with a man flat on his back. On the ticks where one was available the
 * nearest opponent was a median 5.6 m away — that is, most of them were men standing alone
 * under a dropping ball, who in football head it or take it on the chest. Four metres keeps the
 * fifth of them where somebody is genuinely on top of him; three metres would keep a
 * fourteenth.
 *
 * The shooting branch is deliberately NOT gated on this. An unmarked man with his back to goal
 * in the penalty area tries an overhead kick precisely because nobody is near him.
 */
const BICYCLE_PRESSURE_M = 4.0;

/**
 * The overhead kick — one clip, `shot/idle/180_bicycle`, that nothing had ever selected.
 *
 * Measured over four matches before this: chosen 0.00 times and connected 0.00 times. What it
 * needs is narrow, and every part of it is the clip's own doing rather than a choice made here:
 *
 *   - it was drawn from a STANDSTILL, so the speed filter throws it out for anyone above 1.8
 *     m/s, and this decision is offered only to a man already under that;
 *   - it sends the ball STRAIGHT BACKWARDS, and the filter allows 45 degrees either side, so it
 *     can only ever be selected by a man who is pointed away from where the ball is being sent.
 *     That is not tested here: the filter tests it, and a command that reaches no clip simply
 *     falls through to the next one in his queue;
 *   - it wants the ball over his head a third of a second from now, which is what this decides.
 *
 * TWO THINGS ARE WORTH HOOKING A BALL OVER YOUR OWN HEAD FOR, and the second is much the
 * commoner. One is a shot: back to goal in the penalty area, which is the kick everybody
 * pictures. The other is a clearance: a defender who has turned towards his own goal to chase a
 * ball dropping over him, and hooks it back up the pitch rather than heading it towards his own
 * keeper. Measured over four matches, the first situation arises for 0 ticks a match and the
 * second for 148 — which is why a first version of this that only shot produced one overhead
 * kick in thirty-two matches, and why most of the ones in real football are defensive.
 *
 * IT CANNOT STEAL ANYTHING, and that is what makes it safe to put at the head of the queue. A
 * standing man has six shot clips in the whole library: the bicycle and its mirror, and four
 * that want the ball on the grass at 0.11 m (`shot/idle/020`, `shot/idle/090` and their
 * mirrors). At the height this decision fires at, no clip but the bicycle can pass the reach
 * search's height gate, so the command either produces an overhead kick or produces nothing.
 */
export function bicycleCommands(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
  movement: DesiredMovement,
): PlayerCommand[] {
  if (match.ballRetainer || self.role === 'GK') return [];
  const teamDesignated = match.team(self.teamId).designatedPlayer === self;
  if (match.designatedPossessionPlayer !== self && !teamDesignated) return [];
  if (self.movement.length() >= IDLE_DRIBBLE_SWITCH) return [];

  const contact = image.ballPrediction(BICYCLE_CONTACT_MS, _bicycleBall);
  const wantedHeight = self.reachableBallHeight * BICYCLE_BALL_HEIGHT_RATIO;
  if (Math.abs(contact.z - wantedHeight) > BICYCLE_HEIGHT_SLACK_M) return [];
  if (Math.hypot(contact.x - self.position.x, contact.y - self.position.y) > BICYCLE_REACH_M) {
    return [];
  }

  const side = match.team(self.teamId).side;

  // The ordinary shot decision first, so an overhead kick at goal is aimed, powered and turned
  // down for the same reasons every other shot is — including its range gate, which is what
  // keeps the shooting version to the penalty area. The clip's own `touch_maxpowerfactor` of
  // 0.4 then takes most of the power back off it, which is right: nobody strikes one cleanly.
  const shot = shotCommand(match, self, image, movement);
  if (shot?.touchInfo) {
    // AIMED AT THE MIDDLE OF THE GOAL RATHER THAN AT A POST, and that is what decides whether
    // it happens at all. An ordinary shot picks the better of the two posts and misses it by a
    // little on purpose, which swings the aim by up to twenty degrees; the clip will only be
    // selected if the ball leaves within forty-five degrees of straight back over his head, and
    // measured over two matches the men who could have played one were pointed 138 to 142
    // degrees off the goal — inside the tolerance, but with no twenty degrees to spend on
    // picking a corner. Which is also the football: nobody places an overhead kick.
    shot.touchInfo.desiredDirection = normalizedOr(
      new Vector3((PITCH.halfLength + 1) * -side, 0, 0).sub(self.position).setZ(0),
      shot.touchInfo.desiredDirection,
    );
    return [shot];
  }

  // Otherwise it is a clearance — but only as a last resort, which is the whole of what keeps
  // it from happening every three minutes. A man alone under a dropping ball heads it or takes
  // it on his chest; he throws himself at it upside down when somebody is on top of him and
  // there is no time to be tidy. See `BICYCLE_PRESSURE_M`.
  if (self.closestOpponentDistance > BICYCLE_PRESSURE_M) return [];

  // It goes along the line running from his own goal through him — the same rule a headed
  // clearance uses, and for the same reason: aiming every one of them up the middle of the
  // pitch instead was measured to halve the throw-ins in the game. He is only pointed the right
  // way for this if he has turned back towards his own goal, which is the situation the act
  // belongs to, and the clip's own direction filter is what tests it.
  const ownGoal = new Vector3(side * PITCH.halfLength, 0, 0);
  const away = normalizedOr(
    self.position.clone().sub(ownGoal).setZ(0),
    new Vector3(-side, 0, 0),
  );
  return [strikeCommand('shot', movement, away, 1)];
}

const _bicycleBall = new Vector3();

/**
 * The lowest ball he will head rather than chest, in metres.
 *
 * Chosen rather than derived, but only just: the library's chest clips want the ball between
 * 1.21 and 1.57 m and its header clips between 1.58 and 1.89, so a metre and a half is where
 * the top of a chest stops and a forehead starts. Lower and a headed pass takes balls a chest
 * control should have had; higher and there is a band of ball nobody can play at all.
 */
const HEADER_FLOOR_M = 1.55;

/**
 * How good the best team-mate has to look before a header is aimed at him rather than simply
 * hoofed away from his own goal. Most headers in football are clearances, so this is not shy.
 */
const HEADER_PASS_MINIMUM = 0.4;

/**
 * How far a header can miss its aim at zero heading skill, on the pass branch: about 17°.
 * Chosen, not measured.
 */
const HEADER_PASS_ERROR_RAD = 0.3;

/**
 * How far a header can miss its aim at zero heading skill, on the shot branch: about 8.6°,
 * smaller than the pass error because `shotCommand` already applies its own `technical_shot`
 * noise on top. Chosen, not measured.
 */
const HEADER_SHOT_ERROR_RAD = 0.15;

/**
 * What a man does with a ball arriving above his chest — the decision the game never had.
 *
 * The library ships thirteen header clips and NOT ONE of them is a trap or a ball control,
 * which is right, because nobody brings a ball down with his forehead. He heads it somewhere:
 * on to a team-mate, at the goal, or away from his own. So a header can only ever come out of
 * a pass, a shot or an interception — and a man running onto his own side's cross is issued
 * none of the three. `interfereCommand` refuses outright while his own side is winning the
 * ball, and the pass and shot commands are built only for the man already holding it. What he
 * was left with was a chest control and a foot he could not raise, and the measured result was
 * one header a match against real football's forty-odd.
 *
 * So when the ball will be above his chest at the moment he expects to meet it, he is offered
 * a shot if one is on and a pass either way. Nothing below this in the queue can be stolen by
 * it: no clip that meets the ball with a foot reaches above about seventy centimetres, so at
 * this height the only candidates the reach search can accept are the headers themselves.
 */
function headerCommands(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
  movement: DesiredMovement,
): PlayerCommand[] {
  if (match.ballRetainer || self.role === 'GK') return [];
  const teamDesignated = match.team(self.teamId).designatedPlayer === self;
  if (match.designatedPossessionPlayer !== self && !teamDesignated) return [];
  if (image.ballPrediction(self.timeNeeded.usualMs).z < HEADER_FLOOR_M) return [];

  const commands: PlayerCommand[] = [];
  const shot = shotCommand(match, self, image, movement);
  if (shot) {
    // A poor header glances off its aim; a good one plants it precisely. Skill perturbs the
    // horizontal aim only — who wins the ball in the air stays pure geometry, untouched here.
    const shotError =
      (deterministicRandom(match, self.id, 5) * 2 - 1) *
      (1 - self.physics.stat('technical_header')) *
      HEADER_SHOT_ERROR_RAD;
    if (shot.touchInfo) shot.touchInfo.desiredDirection.applyAxisAngle(new Vector3(0, 0, 1), shotError);
    commands.push(shot);
  }

  let best: { target: Vector3; odds: number } | null = null;
  for (const mate of match.team(self.teamId).players) {
    if (mate === self) continue;
    const evaluated = passingOdds(match, self, mate, image, 'shortpass');
    if (!best || evaluated.odds > best.odds) best = evaluated;
  }

  const origin = self.position.clone().addScaledVector(self.movement, 0.12);
  // With nobody worth aiming at he clears it, and a clearance goes along the line running from
  // his own goal through him rather than straight up the middle of the pitch. A man in his own
  // corner therefore heads it towards the corner, which is where a clearance from there
  // actually goes — often enough over the touchline, which is a throw-in and is football.
  // Aiming every clearance at the centre circle instead HALVED the throw-ins in the game.
  const side = match.team(self.teamId).side;
  const ownGoal = new Vector3(side * PITCH.halfLength, 0, 0);
  const towards =
    best && best.odds > HEADER_PASS_MINIMUM
      ? best.target.clone().sub(origin).setZ(0)
      : normalizedOr(origin.clone().sub(ownGoal).setZ(0), new Vector3(-side, 0, 0));
  const aim = autoPass(towards, false);
  const passError =
    (deterministicRandom(match, self.id, 4) * 2 - 1) *
    (1 - self.physics.stat('technical_header')) *
    HEADER_PASS_ERROR_RAD;
  aim.direction.applyAxisAngle(new Vector3(0, 0, 1), passError);
  commands.push(strikeCommand('shortpass', movement, aim.direction, aim.power));
  return commands;
}

function trapCommand(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
  movement: DesiredMovement,
): PlayerCommand | null {
  const horizontalSpeed = Math.hypot(match.ball.momentum.x, match.ball.momentum.y);
  if (horizontalSpeed < 2 || match.ballRetainer || image.ballPrediction(200).distanceTo(self.position) > 10 || self.hasPossession) return null;
  const designated = match.designatedPossessionPlayer === self;
  const teamCandidate =
    match.team(self.teamId).designatedPlayer === self &&
    self.timeNeeded.optimisticMs < 1000 &&
    match.opponentTeam(self.teamId).timeNeededToGetToBallMs > 400 &&
    !match.opponentTeam(self.teamId).hasPossession &&
    duelLikeliness(match, self, image) >= 0.5;
  if (!designated && !teamCandidate) return null;
  if (self.role === 'GK') {
    return actionCommand('trap', {
      direction: new Vector3(-match.team(self.teamId).side, 0, 0),
      velocity: 0,
      lookAt: movement.lookAt,
    });
  }
  return actionCommand('trap', movement);
}

function interfereCommand(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
  movement: DesiredMovement,
  byAnyMeans = false,
): PlayerCommand | null {
  if (match.ballRetainer || image.ballPrediction(200).distanceTo(self.position) > 10) return null;
  const duelFloor = underAllOutOrder(match, self) ? ALL_OUT_INTERFERE_DUEL : 0.2;
  if (
    match.bestPossessionTeamId === self.teamId ||
    (!byAnyMeans && duelLikeliness(match, self, image) < duelFloor)
  ) return null;
  // How narrowly the selector may then pick a clip. An ordinary interception is held to
  // clips heading close to where he was told to go; one made by any means available — which
  // only a person holding the close-down button ever asks for — is allowed to reach much
  // further off that line. `playercontroller.cpp:379-381` sets exactly this, either way round.
  return { ...actionCommand('interfere', movement), strictMovement: byAnyMeans ? 'false' : 'true' };
}

/**
 * The AI's sliding tackle, `playercontroller.cpp:389-414`.
 *
 * The possession test is HIS OWN claim to the ball, not his side's: the original reads the
 * controller's `possessionAmount` (`playercontroller.cpp:616`), which measures the opposition's
 * time to the ball against *his* time to it, with a 200 ms offset. His side's ratio is a
 * different number built from both sides' quickest man and a 1500 ms offset (`team.cpp:324`),
 * and it sits roughly a second higher. Reading the team number here asks that all eleven be
 * more than about 1.7 seconds from a ball he himself is a metre away from, which never
 * happens while a tackle is still worth making, so the AI never slid at all.
 */
function slidingCommand(match: Match, self: MatchPlayer, image: MentalImage): PlayerCommand | null {
  const allOut = underAllOutOrder(match, self);
  if (
    duelLikeliness(match, self, image) < (allOut ? ALL_OUT_SLIDING_DUEL : 0.7) ||
    match.bestPossessionTeamId === self.teamId ||
    playerPossessionAmount(match, self) >= (allOut ? ALL_OUT_SLIDING_POSSESSION : 0.6) ||
    match.designatedPossessionPlayer === self ||
    !match.opponentTeam(self.teamId).hasPossession
  ) return null;
  const ball = match.mentalImage(20).ballPrediction(200).setZ(0);
  // BOTH ENDS OF THIS MEASUREMENT ARE TWO TENTHS OF A SECOND FROM NOW. The ball is asked where
  // it will be in 200 ms, so the man has to be asked the same question, and the original asks
  // it: `playerPos = player->GetPosition() + player->GetMovement() * 0.2`
  // (`playercontroller.cpp:397`). Measuring a future ball against a present man overstates the
  // gap by everything a closing defender covers in those two tenths — at a sprint, a metre and
  // a half, which is the whole width of the window this then tests against. Measured over
  // twenty-five minutes of football: the decision survived its five preconditions 7,848 times
  // and was rejected as "too far from the ball" 7,771 of them. The other 77 were too close or
  // caught the man already on it. NOT ONE SLIDING TACKLE WAS ATTEMPTED, so there were no
  // fouls to speak of either — a tackle from behind is where they come from.
  const from = self.position.clone().addScaledVector(self.movement, 0.2);
  const distance = ball.distanceTo(from);
  const opponent = match.opponentTeam(self.teamId).designatedPlayer;
  const shooting = opponent.anim.currentFunctionType === 'shot' && opponent.touchPending();
  if (!((distance > 0.7 && distance < 1.6 && opponent.timeNeeded.usualMs > 260) || (distance > 0.6 && distance < 1.8 && shooting))) return null;
  const target = ball.addScaledVector(opponent.movement, 0.2);
  const desired = {
    direction: normalizedOr(target.clone().sub(from), self.directionVec),
    velocity: SPRINT_VELOCITY,
    lookAt: target,
  };
  return actionCommand('sliding', desired);
}

function duelLikeliness(match: Match, self: MatchPlayer, image: MentalImage): number {
  const opponent = match.opponentTeam(self.teamId).designatedPlayer;
  const oi = image.player(opponent.id);
  if (!oi) return 1;
  const me = self.position.clone().addScaledVector(self.movement, 0.1);
  const opp = oi.position.clone().addScaledVector(oi.movement, 0.1);
  const meToOpp = normalizedOr(opp.clone().sub(me), self.directionVec);
  const oppToBall = normalizedOr(image.ballPrediction(100).setZ(0).sub(opp), oi.directionVec);
  return 1 - (meToOpp.dot(oppToBall) * 0.5 + 0.5);
}

/** Where the keeper stands when the ball is in the other half — `goalie_default.cpp:44`. */
const KEEPER_LINE_DISTANCE = 10;

/**
 * How far up his line the goalkeeper comes while the OTHER SIDE has the ball, as a fraction of
 * the way to the threat.
 *
 * DIVERGENCE, AND WHAT IT REPLACES. The original works his depth out from a bias that starts at
 * 0.3 and is then multiplied by `NormalizedClamp(fadingTeamPossessionAmount, 1.0, 1.5)`
 * (`goalie_default.cpp:85`), and that clamp is exactly zero for every possession figure below 1
 * — which is the whole of an opposing attack. So his keeper falls to the floor of the offset,
 * 1.4 m off his own goal line, on the tick the ball crosses the halfway line, and the ten metres
 * he was standing at a tick earlier vanish in one step. Measured over eight matches, his median
 * depth with the ball anywhere in his own half and the other side on it is 1.4 m at every range
 * from the edge of the box to the halfway line: he is not positioning at all, he is parked.
 *
 * THE FRACTION IS PINNED BY THE TEN METRES ON THE OTHER SIDE OF THAT LINE rather than chosen.
 * A fifth of the way to the ball puts him 10.9 m out with it on the halfway line, and the
 * original's own rule puts him at 10 one metre further away, so the two meet and the step is
 * gone. Any smaller fraction rebuilds the step facing the other way.
 *
 * What it does NOT do on its own is keep him out of the game — a keeper five or six metres up
 * his line cuts out half the through balls in football. That is `KEEPER_SHOOTING_RANGE_M`'s
 * job, and it is what decides how much of this fraction he ever gets to use: it is only beyond
 * the range this game shoots from that any of it survives.
 */
const KEEPER_DEPTH_FRACTION = 0.2;

/**
 * How near his goal a threat has to be before the goalkeeper stops advancing on it and stands
 * on his line instead, in metres.
 *
 * THIS IS THE HALF THAT ANSWERS THE EVIDENCE AGAINST A DEEPER KEEPER, and it is the whole of
 * what makes the fraction above safe. Inside this distance he refuses to leave his line at all;
 * beyond it his depth is what is left of the fraction once this much has been taken off, so it
 * comes up smoothly rather than switching on.
 *
 * MEASURED, NOT CHOSEN, AND THE THING MEASURED IS WHERE THIS GAME ACTUALLY SHOOTS FROM. Over
 * six matches every shot on target was struck from inside 28.1 m of goal, 95% of them from
 * inside 19.1 m, and the median was 17.1 m. So 28 m is the range at which he can still be shot
 * at, and standing on his line for all of it costs him nothing he could have had.
 *
 * IT IS ALSO THE ONE DIAL THE WHOLE CHANGE ANSWERS TO, and the response is steep. Eight matches
 * at each value, against 9.4 shots a match for the keeper this replaces:
 *
 *     13.75 m    3.6 shots a match     he sweeps up half the attacks in the game
 *     20 m       7.3
 *     23.75 m    8.1
 *     26.25 m    8.6
 *     30 m       8.6                   no further gain: the shots have all come back
 *
 * The cost of setting it short is not that he saves more — it is that standing five or six
 * metres up his line he cuts out the through balls and cut-backs that used to reach a forward,
 * and he does it to half of them. Measured at 13.75 m he took twice as much of the ball as the
 * keeper he replaces, 69 seconds a match against 30, and intervened 30.5 times a match against
 * 18.8. It is not the charge — adding back the head start his depth gives him before he decides
 * to come for a ball left four whole matches bit for bit identical.
 */
const KEEPER_SHOOTING_RANGE_M = 28;

/**
 * Where the goalkeeper wants to stand, `goalie_default.cpp:41-224`.
 *
 * Two quite different jobs. When nothing is heading for the goal he stands on the line that
 * splits the goal into equal halves as seen from the ball, and how far up that line he comes
 * is the whole of his character: about a third of the way as a rule, further the more securely
 * his side holds the ball, and all the way out to meet it when an opponent is running at him
 * and no defender is going to get there first — tempered again by how dangerous that
 * opponent's nearest support is. When a shot IS heading for the goal he stops positioning
 * altogether and goes to the nearest point on its path, unless it will be over his head by
 * the time it arrives, in which case he covers the line instead.
 *
 * With the ball in the opposite half he does none of this and simply stands ten metres off
 * his own goal line.
 */
function goalkeeperMovement(match: Match, self: MatchPlayer, image: MentalImage): DesiredMovement {
  const team = match.team(self.teamId);
  const side = team.side;
  const goalLineX = PITCH.halfLength * side;
  const goal = new Vector3(goalLineX, 0, 0);
  const toGoalFallback = new Vector3(side, 0, 0);
  const edge = PITCH.halfLength - 0.2;

  let maxVelocity = SPRINT_VELOCITY;
  let target = new Vector3((PITCH.halfLength - KEEPER_LINE_DISTANCE) * side, 0, 0);

  const ball = image.ballPrediction(600 + self.timeNeeded.usualMs * 0.2).setZ(0);
  if (ball.x * side > 0) {
    const { bound, intersectionY } = goalkeeperGoalThreat(match, self, image);

    if (!bound) {
      // The bisector of the two post directions, run out to just short of the back line.
      // Standing exactly on the line is deliberately avoided: some save animations only
      // reach the ball once it is already behind it, which would be no use at all.
      const middle = normalizedOr(
        normalizedOr(new Vector3(goalLineX, GOAL.halfWidth, 0).sub(ball), toGoalFallback).add(
          normalizedOr(new Vector3(goalLineX, -GOAL.halfWidth, 0).sub(ball), toGoalFallback),
        ),
        toGoalFallback,
      );
      const backLineX = (PITCH.halfLength - 0.7) * side;
      const travel = Math.abs(middle.x) < 1e-6 ? 0 : (backLineX - ball.x) / middle.x;
      const onLine = new Vector3(
        backLineX,
        clamp(ball.y + middle.y * travel, -GOAL.halfWidth, GOAL.halfWidth),
        0,
      );

      // How far up that line he comes, and from where he measures it. A fifth of the way to
      // the threat while the other side has the ball, rising to the original's own third as
      // his side takes hold of it: `goalie_default.cpp:85` ramps 0 to 0.3 over exactly this
      // range of the possession figure, and starting the ramp at the depth he already had
      // rather than at nothing is what keeps him from snapping back to his line the moment
      // his side edges ahead on it.
      let origin = ball.clone();
      let bias =
        KEEPER_DEPTH_FRACTION +
        (0.3 - KEEPER_DEPTH_FRACTION) * normalizedClamp(team.fadingPossessionAmount, 1, 1.5);
      // Set when he has decided to leave his goal ON PURPOSE, for a ball he means to win.
      // That is the one case the warning cap below must not touch, because the whole point of
      // it is to go where a shot could beat him.
      let charging = false;

      if (team.fadingPossessionAmount < 1) {
        const opponent = match.opponentTeam(self.teamId).designatedPlayer;
        const oppPos = opponent.position.clone().addScaledVector(opponent.movement, 0.32);
        // A man who is not yet in control of the ball is the greater part of the threat;
        // once he has it under control the ball itself is.
        origin = opponent.hasPossession
          ? oppPos.clone().multiplyScalar(0.4).addScaledVector(ball, 0.6)
          : oppPos.clone().multiplyScalar(0.6).addScaledVector(ball, 0.4);

        const oppGap = shootingPointGap(oppPos, goal, 20);
        const mate = closestPlayer(team.players, oppGap.point, self);
        const mateGap = mate
          ? mate.position.clone().addScaledVector(mate.movement, 0.24).distanceTo(oppGap.point)
          : 99999;

        // He comes for a BALL HE CAN GET TO, not for a man who already has it.
        //
        // DIVERGENCE, AND THE REASON FOR IT. The original asks one question before charging:
        // will a team-mate reach the point where this opponent can shoot from before the
        // opponent does? It never asks whether the keeper himself can get there, and its
        // notion of where an opponent must be before he is dangerous is about ten metres out
        // — half of a twenty-metre threshold. This game's shooters fire from up to twenty
        // metres, so by the time the keeper sets off, the man he is running at can already
        // shoot, and does. Measured over six whole matches: on eleven shots the keeper was
        // five metres or more off his line, and he had started the second before that on his
        // line and was sprinting outwards at six to seven metres a second when the ball was
        // struck. Seven of those eleven were goals — 64% against 9% for every other shot in
        // the same matches, which is real football's own figure — and he dived at one of
        // them, because a ball crossing the line seven metres away is not divable. His claim
        // to the ball on all eleven was 0.20 to 0.41: he was never going to win it.
        //
        // There is also no upside to weigh against that here. A real keeper who comes out
        // narrows the angle and can smother the ball at a striker's feet; this game has
        // neither — a shot is aimed at a fixed point beside the post whatever the keeper
        // does, and the only save he owns is the dive. So the condition added is the one a
        // sweeper-keeper actually applies: come for a through ball or a loose ball you are
        // first to, and stay at home for a man in possession.
        const canWinTheBall = playerPossessionAmount(match, self) >= 1;
        if (canWinTheBall && mateGap > oppGap.distance + 1) {
          // Nobody is going to cut this out and he gets there first: come and meet it.
          bias = 1;
          charging = true;
          const helper = closestPlayer(
            match.opponentTeam(self.teamId).players,
            goal,
            opponent,
          );
          if (helper) {
            const helperPos = helper.position.clone().addScaledVector(helper.movement, 0.32);
            const helperGap = shootingPointGap(helperPos, goal, 24);
            const helperMate = closestPlayer(team.players, helperGap.point, self);
            const helperMateGap = helperMate
              ? helperMate.position
                  .clone()
                  .addScaledVector(helperMate.movement, 0.24)
                  .distanceTo(helperGap.point)
              : 99999;
            // Then think better of it in proportion to how well placed that opponent's best
            // support is: leaving the goal to meet one man is no good if the pass beats you.
            const secondary =
              helperMateGap > helperGap.distance
                ? normalizedClamp(helperMateGap - helperGap.distance, 0, 2)
                : 0;
            const ratio =
              (1 - normalizedClamp(helperGap.distance / (oppGap.distance + 0.0001), 1, 1.5)) * 0.7;
            bias = clamp(1 - secondary * ratio, 0, 1);
          }
        }
      }

      // The order to come out simply pins that bias at its maximum.
      if (match.timeMs < team.keeperRushUntilMs) {
        bias = 1;
        charging = true;
      }

      const threat = Math.max(origin.distanceTo(onLine) - 0.5, 0);
      if (!charging) {
        // WHAT PULLS HIM HOME AGAIN. He will not stand so far up his line that a shot struck
        // from where the threat is now would reach him with less warning than a dive needs.
        // That caps his depth at the threat's distance less about fourteen metres, so inside
        // the box he is on his line whatever the fraction above says, and it is only out at
        // twenty-five or thirty metres that the fraction is what binds.
        //
        // The warning he insists on fades as his own side takes hold of the ball, over exactly
        // the possession range the fraction rises across, because the thing it is protecting
        // him from is an opponent striking the ball and there is nobody to strike it. Without
        // that, a keeper whose defenders were playing out from the back in front of him would
        // have been pinned to his line by a shot nobody was going to take — shallower than the
        // original leaves him, in the one case the original has right.
        const shelter = 1 - normalizedClamp(team.fadingPossessionAmount, 1, 1.5);
        const warned = Math.max(threat - KEEPER_SHOOTING_RANGE_M * shelter, 0);
        bias = threat < 1e-6 ? 0 : Math.min(bias, warned / threat);
      }

      const offset = clamp(threat * bias, 0.7, PITCH.halfLength);
      target = onLine
        .clone()
        .addScaledVector(normalizedOrZero(origin.clone().sub(onLine)), offset);

      // On the way back to goal and already close to the right line: walk, so that the body
      // ends up facing the way a save needs it to rather than the way a sprint leaves it.
      const back = lineProjection(origin, onLine, self.position);
      if (
        target.distanceTo(goal) < self.position.distanceTo(goal) &&
        back.distance < 1 &&
        back.u > 0
      ) {
        maxVelocity = WALK_VELOCITY;
      }
      target.x = clamp(target.x, -edge, edge);
    } else {
      // A shot is coming. Meet it on its own path, held 0.4 m clear of the goal line for the
      // same reason as above.
      const from = image.ballPrediction(10).setZ(0);
      const onGoalLine = new Vector3(goalLineX, intersectionY, 0);
      onGoalLine.addScaledVector(normalizedOrZero(from.clone().sub(onGoalLine)), 0.4);

      const mine = lineProjection(from, onGoalLine, self.position.clone().addScaledVector(self.movement, 0.05));
      const inASecond = lineProjection(from, onGoalLine, image.ballPrediction(1010).setZ(0));

      let overHead = false;
      if (inASecond.u > 1e-4) {
        const arrivalMs = 10 + 1000 * (mine.u / inASecond.u);
        // The original reads this through an unsigned parameter, so a ball that has already
        // passed the keeper — a negative time — asks for the far end of the forecast rather
        // than for now. Preserved: it decides whether he covers the line or chases the path.
        const sample = arrivalMs < 0 ? BALL_PREDICTION_MS - TICK_MS : arrivalMs;
        if (image.ballPrediction(sample).z > GOAL.height) overHead = true;
      }

      if (overHead) {
        target = onGoalLine;
      } else {
        target = from.clone().lerp(onGoalLine, clamp(mine.u, 0, 1)).setZ(0);
        target.x = clamp(target.x, -edge, edge);
      }
    }
  }

  const toTarget = target.clone().sub(self.position);
  return {
    direction: normalizedOr(toTarget, self.directionVec),
    velocity: clamp(toTarget.length() * 2.6, IDLE_VELOCITY, maxVelocity),
    lookAt: image.ballPrediction(0).setZ(0),
  };
}

/**
 * How far a player still has to travel before he is close enough to shoot, and where that
 * point is (`goalie_default.cpp:99-105`). The threshold shrinks as he closes in, so a man
 * already inside it has nothing left to travel and is treated as able to shoot now.
 */
function shootingPointGap(
  from: Vector3,
  goal: Vector3,
  threshold: number,
): { point: Vector3; distance: number } {
  const toGoal = goal.distanceTo(from);
  const distance = clamp(
    toGoal - threshold * normalizedClamp(toGoal, 0, threshold * 2),
    0,
    PITCH.halfLength,
  );
  return {
    point: from.clone().addScaledVector(normalizedOrZero(goal.clone().sub(from)), distance),
    distance,
  };
}

/**
 * Where a point falls against a segment: `u` is 0 at the start and 1 at the end, unclamped,
 * and the distance is measured to that unclamped point (`line.cpp:60-81`).
 */
function lineProjection(a: Vector3, b: Vector3, point: Vector3): { u: number; distance: number } {
  const span = b.clone().sub(a).setZ(0);
  const lengthSq = span.lengthSq();
  if (lengthSq < 1e-12) return { u: 0, distance: a.distanceTo(point) };
  const u = point.clone().setZ(0).sub(a).dot(span) / lengthSq;
  return { u, distance: a.clone().addScaledVector(span, u).distanceTo(_flat.copy(point).setZ(0)) };
}

const _flat = new Vector3();

/**
 * Is this ball on its way into the goal, as far as this keeper can tell?
 * `GoalieDefaultStrategy::CalculateIfBallIsBoundForGoal` (`goalie_default.cpp:228-286`).
 *
 * Purely a flat question: the height is never looked at, so a ball climbing over the bar still
 * reads as bound for goal. The effective goal is widened by how poorly the keeper reads the
 * game, from a couple of per cent for the best of them to half again for the worst.
 */
export function goalkeeperGoalThreat(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
): { bound: boolean; intersectionY: number } {
  const side = match.team(self.teamId).side;
  const goalLineX = PITCH.halfLength * side;
  const ball0 = image.ballPrediction(0).setZ(0);
  const ball800 = image.ballPrediction(800).setZ(0);
  const deltaX = ball800.x - ball0.x;
  const intersectionY =
    Math.abs(deltaX) < 1e-6
      ? 1000
      : ball0.y + ((goalLineX - ball0.x) / deltaX) * (ball800.y - ball0.y);
  const panic =
    1.02 +
    (1 -
      (self.physics.stat('mental_defensivepositioning') * 0.6 +
        self.physics.stat('mental_vision') * 0.4)) *
      0.5;
  // The original will not even ask the question about a ball more than 32 m away — it calls
  // that a processor saving, but it is also what stops him abandoning his position for a shot
  // struck from the far end of the pitch. Measured in three dimensions, as there.
  const near = self.position.distanceTo(image.ballPrediction(250)) < 32;
  return {
    bound:
      near &&
      image.ballPrediction(BALL_PREDICTION_MS - TICK_MS).x * side > PITCH.halfLength &&
      Math.abs(intersectionY) <= GOAL.halfWidth * panic,
    intersectionY,
  };
}

/**
 * The keeper's hands, `playercontroller.cpp:239-259`.
 *
 * The back-pass test is the fiddly part and reads three separate things: the ball was last
 * touched by his own side, that touch was not his own, and his side is the one that last
 * kicked the ball deliberately. The middle condition is what lets him gather his own
 * clearance if it comes straight back off a post; the third looks at a record kept per kind
 * of touch, so a team-mate's accidental bump after a back-pass does not make it legal.
 */
/**
 * Is this a ball his own side deliberately played back to him, which he may not pick up?
 *
 * Three conditions, and each of the last two is doing work. His own side touched it last; it
 * was not HIM who touched it, so he may gather his own clearance coming back off a post; and
 * the record is kept per kind of touch, so a team-mate's accidental bump after a genuine back
 * pass does not launder it into a ball he may handle.
 */
export function isBackPassTo(match: Match, keeper: MatchPlayer): boolean {
  return (
    match.lastTouchTeamId === keeper.teamId &&
    match.lastTouchPlayer() !== keeper &&
    match.lastTouchTeamIdOfType(TouchType.IntentionalKicked) === keeper.teamId
  );
}

function keeperDeflectCommand(
  match: Match,
  self: MatchPlayer,
  image: MentalImage,
): PlayerCommand | null {
  const team = match.team(self.teamId);
  if (self.role !== 'GK' || match.ballRetainer || image.ballPrediction(400).distanceTo(self.position) > 20) return null;
  if (isBackPassTo(match, self)) return null;
  const ball = image.ballPrediction(160);
  if (Math.abs(ball.y) > 20.05 || ball.x * -team.side > -PITCH.halfLength + 16.4) return null;
  // His own claim to the ball, not his side's: the two are different numbers and only this
  // one says how much quicker *he* is to it than the nearest opponent.
  const claim = playerPossessionAmount(match, self);
  if (self.hasUniquePossession && claim >= 3.4) return null;
  // The dive itself is chosen purely on whether it reaches the ball. Handing it a desired
  // direction and velocity would drag every save towards wherever he wanted to be standing.
  const command: PlayerCommand = {
    desiredFunctionType: 'deflect',
    useDesiredMovement: false,
    desiredDirection: self.directionVec.clone(),
    desiredVelocityFloat: IDLE_VELOCITY,
    useDesiredLookAt: false,
    desiredLookAt: image.ballPrediction(0).setZ(0),
  };
  command.pickupBall = !goalkeeperGoalThreat(match, self, image).bound && claim > 1.3;
  return command;
}

/**
 * What a player is actually willing to run, `elizacontroller.cpp:450-487`.
 *
 * Two separate throttles, and they are not the same idea. The first is how far he is from the
 * action, scaled by his mindset and whether his side has the ball: a defender with his team
 * on the ball, forty metres from play, jogs. Tiredness tightens that window at both ends — a
 * spent player treats the action as far away sooner, so he gives up on more of it.
 *
 * The second is breath: how hard he has run over the last ten seconds, capped by his work
 * rate. It only applies in proportion to how disengaged he already is, so a tired man still
 * sprints when the ball is at his feet. That is the difference between a player who paces
 * himself and one who simply becomes slow.
 */
function lazyVelocity(match: Match, self: MatchPlayer, requested: number): number {
  let velocity = requested;
  if (velocity > SPRINT_VELOCITY) velocity = SPRINT_VELOCITY + (velocity - SPRINT_VELOCITY) * 0.1;
  const actionDistance = self.position.distanceTo(match.opponentTeam(self.teamId).designatedPlayer.position);
  const teamPossession = clamp(match.team(self.teamId).fadingPossessionAmount - 0.5, 0, 1);
  const mindset = ROLE_OFFENSIVENESS[self.dynamicRole];
  const fresh = self.fatigueFactorInv;
  const byRole = mindset + teamPossession * (1 - mindset * 2);
  const byPosition = normalizedClamp(
    actionDistance,
    20 * (fresh * 0.8 + 0.2),
    65 * (fresh * 0.5 + 0.5),
  );
  const lazy = byPosition * (0.5 + byRole * 0.5);
  let result = velocity * (1 - lazy);
  if (requested >= DRIBBLE_VELOCITY && result < DRIBBLE_VELOCITY) result = DRIBBLE_VELOCITY;

  let breath = 1 - normalizedClamp(self.averageVelocity(VELOCITY_HISTORY_SECONDS), 0, SPRINT_VELOCITY);
  breath = Math.pow(breath, 0.8 - self.physics.stat('mental_workrate') * 0.2);
  breath = clamp(breath * 1.2, 0, 1);
  breath = breath * lazy + (1 - lazy);
  return Math.min(result, SPRINT_VELOCITY * breath);
}

interface ForceSpot {
  origin: Vector3;
  power: number;
  scale: number;
  repel: boolean;
  exponent: number;
}

function forceField(spots: ForceSpot[], position: Vector3, dampingDistance: number): Vector3 {
  const result = new Vector3();
  let total = 0;
  for (const spot of spots) {
    const delta = spot.origin.clone().sub(position);
    const distance = delta.length();
    const intensity = Number.isFinite(spot.scale)
      ? Math.pow(clamp(1 - distance / spot.scale, 0, 1), spot.exponent)
      : 1;
    if (intensity <= 0) continue;
    const direction = normalizedOrZero(delta);
    if (spot.repel) direction.negate();
    else if (distance < dampingDistance) direction.multiplyScalar(distance / dampingDistance);
    const force = spot.power * intensity;
    result.addScaledVector(direction, force);
    total += force;
  }
  return total === 0 ? result : result.divideScalar(total).multiplyScalar(SPRINT_VELOCITY);
}

function nearestImages(
  players: MatchPlayer[],
  image: MentalImage,
  target: Vector3,
  count: number,
): MentalImagePlayer[] {
  return players
    .map((player) => image.player(player.id))
    .filter((player): player is MentalImagePlayer => player !== undefined)
    .sort((a, b) => a.position.distanceTo(target) - b.position.distanceTo(target))
    .slice(0, count);
}

function normalizedToMax(vector: Vector3, maximum: number): Vector3 {
  const length = vector.length();
  if (length === 0 || maximum <= 0) return new Vector3();
  return length > maximum ? vector.multiplyScalar(maximum / length) : vector;
}

/**
 * The AI's coin flips: where in the goal to aim a penalty, whether to chip a free kick, how
 * far off a shot goes.
 *
 * The original draws these from the engine's process-wide generator. This does not, and the
 * difference is deliberate. The value is a hash of the run's seed, the tick, the player and
 * which decision is being made, so it depends on nothing else: adding, removing or reordering
 * a decision somewhere else in the match leaves every other flip in the match untouched. A
 * shared stream has the opposite property — one extra draw shifts every value after it — and
 * that is exactly the property that makes the original's own runs stop matching each other,
 * since ten AI threads consume its generators in an order nothing fixes.
 *
 * The run's seed is folded in, so a fixture can be replayed exactly (same seed) or played
 * differently (any other seed). Seed 0 is the default and reproduces the earlier behaviour
 * unchanged, because folding it in is an exclusive-or with zero.
 */
function deterministicRandom(match: Match, id: number, salt: number): number {
  let value =
    Math.imul((match.timeMs / 10) | 0, 0x45d9f3b) ^
    Math.imul(id + salt * 97, 0x27d4eb2d) ^
    Math.imul(match.randomSeed, 0x9e3779b1);
  value ^= value >>> 16;
  return (value >>> 0) / 0x100000000;
}
